import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import { useHistoryBackClose } from "../hooks/useHistoryBackClose";
import { createPortal } from "react-dom";
import api from "../services/api";
import { downloadPdf } from "../utils/downloadPdf";
import { useToast } from "./ToastProvider";
import ModalHeader from "./ModalHeader";
import SplitDateInput from "./SplitDateInput";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { themeColors } from "../utils/theme";
import { todayLocalISO } from "../utils/date";

import {
  faXmark,
  faPlus,
  faTrashCan,
  faPenToSquare,
  faFloppyDisk,
  faAppleWhole,
  faUtensils,
  faChartPie,
  faUser,
  faRightLeft,
  faCopy,
  faWandMagicSparkles,
  faSpinner,
  faCheck,
  faTriangleExclamation,
  faLightbulb,
  faBolt,
  faFire,
  faDrumstickBite,
  faBowlRice,
  faDroplet,
  faFilePdf,
} from "@fortawesome/free-solid-svg-icons";

const SUPPORTS_BACKDROP_FILTER = typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('backdrop-filter', 'blur(1px)');

// ─── Macro helpers ────────────────────────────────────────────────────────────

function effectiveG(item) {
  const qty = item.quantity_g || 0;
  if (item.unit === "ml") {
    const density = item.food?.density_g_per_ml ?? 1.0;
    return qty * density;
  }
  if (item.unit === "un") {
    const unitW = item.food?.unit_weight_g ?? 100.0;
    return qty * unitW;
  }
  return qty;
}

function calcMacros(meals, weightKg) {
  let pa = 0, pv = 0, carbs = 0, fiber = 0, fat = 0;
  for (const meal of meals) {
    for (const item of meal.items) {
      const f = item.food;
      if (!f) continue;
      const r = effectiveG(item) / 100;
      pa    += (f.protein_animal_g || 0) * r;
      pv    += (f.protein_veg_g    || 0) * r;
      carbs += (f.carbs_g          || 0) * r;
      fiber += (f.fiber_g          || 0) * r;
      fat   += (f.fat_g            || 0) * r;
    }
  }
  const pt = pa + pv;
  const kcalP = pt * 4;
  const kcalC = carbs * 4;
  const kcalF = fat * 9;
  const total = kcalP + kcalC + kcalF;
  const pct = (v) => total > 0 ? (v / total * 100).toFixed(1) : "0.0";
  const gkg = (v) => weightKg ? (v / weightKg).toFixed(1) : "-";
  return {
    protein_total:  { g: pt.toFixed(0),    pct: pct(kcalP), kcal: Math.round(kcalP),  gkg: gkg(pt)    },
    protein_animal: { g: pa.toFixed(0) },
    protein_veg:    { g: pv.toFixed(0) },
    carbs:          { g: carbs.toFixed(0), pct: pct(kcalC), kcal: Math.round(kcalC),  gkg: gkg(carbs) },
    fiber:          { g: fiber.toFixed(0) },
    fat:            { g: fat.toFixed(0),   pct: pct(kcalF), kcal: Math.round(kcalF),  gkg: gkg(fat)   },
    kcal_total:     Math.round(total),
  };
}

// ─── Recentes (localStorage) ──────────────────────────────────────────────────

const RECENTS_KEY = "nutrition_recent_foods";
function getRecentIds() {
  try { return JSON.parse(localStorage.getItem(RECENTS_KEY) || "[]"); } catch { return []; }
}
function pushRecentId(id) {
  const prev = getRecentIds();
  const next = [id, ...prev.filter((x) => x !== id)].slice(0, 8);
  try { localStorage.setItem(RECENTS_KEY, JSON.stringify(next)); } catch {}
}

// ─── FoodPicker - typeahead com recentes ──────────────────────────────────────

function FoodPicker({ foods, onSelect, placeholder = "Buscar alimento…", autoFocus = false }) {
  useBodyScrollLock();
  const [query, setQuery]           = useState("");
  const [open, setOpen]             = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const [recentIds, setRecentIds]   = useState(getRecentIds);
  const [dropPos, setDropPos]       = useState({ top: 0, left: 0, width: 0 });
  const inputRef  = useRef(null);
  const wrapRef   = useRef(null);
  const closeTimer = useRef(null);

  useEffect(() => { if (autoFocus) inputRef.current?.focus(); }, [autoFocus]);

  function updatePos() {
    if (!wrapRef.current) return;
    const r = wrapRef.current.getBoundingClientRect();
    setDropPos({ top: r.bottom + window.scrollY + 4, left: r.left + window.scrollX, width: r.width });
  }

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      const recents = recentIds.map((id) => foods.find((f) => f.id === id)).filter(Boolean);
      return recents.length > 0 ? [{ _header: "Recentes" }, ...recents] : foods.slice(0, 10);
    }
    return foods.filter((f) => f.name.toLowerCase().includes(q)).slice(0, 18);
  }, [query, foods, recentIds]);

  const items = results.filter((r) => !r._header);

  function select(food) {
    pushRecentId(food.id);
    setRecentIds(getRecentIds());
    onSelect(food);
    setQuery("");
    setOpen(false);
  }

  function onKeyDown(e) {
    if (!open) { if (e.key === "ArrowDown" || e.key === "Enter") { updatePos(); setOpen(true); } return; }
    if (e.key === "ArrowDown") { setHighlighted((h) => Math.min(h + 1, items.length - 1)); e.preventDefault(); }
    else if (e.key === "ArrowUp") { setHighlighted((h) => Math.max(h - 1, 0)); e.preventDefault(); }
    else if (e.key === "Enter") { if (items[highlighted]) select(items[highlighted]); e.preventDefault(); }
    else if (e.key === "Escape") { setOpen(false); }
  }

  const iCls = "w-full bg-[var(--border-0-10)] border border-[var(--border-0-15)] rounded px-3 py-1.5 text-sm text-[var(--text-primary)] placeholder-[var(--text-0-50)] focus:outline-none focus:border-[var(--blue)]";

  const dropdown = open && results.length > 0 && createPortal(
    <div
      onMouseDown={(e) => { clearTimeout(closeTimer.current); e.preventDefault(); }}
      style={{
        position: "absolute",
        top: dropPos.top,
        left: dropPos.left,
        width: dropPos.width,
        zIndex: 9999,
        background: "var(--surface-primary)",
        border: "1px solid var(--border-0-15)",
        borderRadius: 10,
        boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
        maxHeight: 280,
        overflowY: "auto",
      }}
    >
      {results.map((r) => {
        if (r._header) return (
          <div key="_hdr" style={{ padding: "6px 12px 2px", fontSize: "var(--fs-xs)", fontWeight: 700, color: "var(--accent)", letterSpacing: "0.08em", textTransform: "uppercase" }}>
            {r._header}
          </div>
        );
        const itemIdx = items.indexOf(r);
        const isHl = itemIdx === highlighted;
        const prot = ((r.protein_animal_g || 0) + (r.protein_veg_g || 0)).toFixed(0);
        return (
          <div
            key={r.id}
            onClick={() => select(r)}
            onMouseEnter={() => setHighlighted(itemIdx)}
            style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "8px 12px", cursor: "pointer",
              background: isHl ? "rgba(var(--accent-rgb),0.14)" : "transparent",
              borderLeft: isHl ? `3px solid ${themeColors.blue}` : "3px solid transparent",
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, color: isHl ? "var(--text-primary)" : "var(--text-muted)", fontWeight: isHl ? 600 : 400, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {r.name}
              </div>
              {r.category && (
                <div style={{ fontSize: "var(--fs-xs)", color: "var(--text-secondary)", marginTop: 1 }}>{r.category}</div>
              )}
            </div>
            <div style={{ fontSize: "var(--fs-xs)", color: "var(--text-secondary)", marginLeft: 10, flexShrink: 0, textAlign: "right" }}>
              P:{prot} C:{r.carbs_g?.toFixed(0)} G:{r.fat_g?.toFixed(0)}
            </div>
          </div>
        );
      })}
    </div>,
    document.body
  );

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <input
        ref={inputRef}
        value={query}
        onChange={(e) => { setQuery(e.target.value); setHighlighted(0); updatePos(); setOpen(true); }}
        onFocus={() => { updatePos(); setOpen(true); }}
        onBlur={() => { closeTimer.current = setTimeout(() => setOpen(false), 160); }}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className={iCls}
        autoComplete="off"
      />
      {dropdown}
    </div>
  );
}

// ─── Barra compacta de macros ─────────────────────────────────────────────────

function MacroBar({ meals, weightKg, targetKcal }) {
  const m = calcMacros(meals, weightKg);
  const pctKcal = targetKcal ? Math.round((m.kcal_total / targetKcal) * 100) : null;
  return (
    <div style={{
      position: "sticky", top: 0, zIndex: 10,
      background: "var(--surface-strong)",
      border: "1px solid rgba(var(--accent-rgb),0.25)",
      borderRadius: 12, padding: "8px 14px",
      display: "flex", flexWrap: "wrap", alignItems: "center", gap: "10px 16px",
      fontSize: 12, fontWeight: 700,
    }}>
      <span style={{ color: "var(--accent)" }}><FontAwesomeIcon icon={faDrumstickBite} /> {m.protein_total.g}g</span>
      <span style={{ color: "var(--input-border-focus)" }}><FontAwesomeIcon icon={faBowlRice} /> {m.carbs.g}g</span>
      <span style={{ color: "var(--feedback-warning)" }}><FontAwesomeIcon icon={faDroplet} /> {m.fat.g}g</span>
      <span style={{ color: "var(--feedback-error)" }}><FontAwesomeIcon icon={faFire} /> {m.kcal_total} kcal</span>
      {pctKcal !== null && (
        <span style={{ marginLeft: "auto", color: pctKcal > 100 ? "var(--feedback-error)" : "var(--text-muted)", fontSize: "var(--fs-xs)", fontWeight: 600 }}>
          {pctKcal}% da meta
        </span>
      )}
    </div>
  );
}

// ─── Tabela de macros ─────────────────────────────────────────────────────────

function MacroTable({ meals, weightKg }) {
  const m = calcMacros(meals, weightKg);

  const hdr = {
    backgroundColor: "var(--surface-secondary)", color: "var(--text-primary)", textAlign: "center",
    padding: "6px 10px", fontSize: "13px", fontWeight: "600", border: "1px solid #555",
  };
  const lbl = {
    padding: "6px 12px", fontWeight: "700", fontSize: "13px",
    border: "1px solid #444", color: "var(--text-primary)", backgroundColor: "var(--surface-secondary)", whiteSpace: "nowrap",
  };
  const sub = { ...lbl, fontWeight: "400", paddingLeft: "24px", color: "var(--text-muted)" };
  const cell = {
    padding: "6px 10px", textAlign: "center", fontSize: "13px",
    border: "1px solid #444", color: "var(--text-primary)", backgroundColor: "var(--surface-primary)",
  };
  const bold = { ...cell, fontWeight: "700", color: "var(--text-primary)", backgroundColor: "var(--surface-secondary)" };

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", width: "100%", border: "2px solid #555" }}>
        <thead>
          <tr>
            <th style={{ ...hdr, textAlign: "left" }}>Macros</th>
            <th style={hdr}>g</th>
            <th style={hdr}>%</th>
            <th style={hdr}>Kcal</th>
            <th style={hdr}>g/kg</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={lbl}>Proteína total</td>
            <td style={bold}>{m.protein_total.g}</td>
            <td style={bold}>{m.protein_total.pct}%</td>
            <td style={bold}>{m.protein_total.kcal}</td>
            <td style={bold}>{m.protein_total.gkg}</td>
          </tr>
          <tr>
            <td style={sub}>Proteína (A)</td>
            <td style={cell}>{m.protein_animal.g}</td>
            <td style={cell}>-</td><td style={cell}>-</td><td style={cell}>-</td>
          </tr>
          <tr>
            <td style={sub}>Proteína (V)</td>
            <td style={cell}>{m.protein_veg.g}</td>
            <td style={cell}>-</td><td style={cell}>-</td><td style={cell}>-</td>
          </tr>
          <tr>
            <td style={lbl}>Carboidratos</td>
            <td style={bold}>{m.carbs.g}</td>
            <td style={bold}>{m.carbs.pct}%</td>
            <td style={bold}>{m.carbs.kcal}</td>
            <td style={bold}>{m.carbs.gkg}</td>
          </tr>
          <tr>
            <td style={sub}>Fibras</td>
            <td style={cell}>{m.fiber.g}</td>
            <td style={cell}>-</td><td style={cell}>-</td><td style={cell}>-</td>
          </tr>
          <tr>
            <td style={lbl}>Lipídios</td>
            <td style={bold}>{m.fat.g}</td>
            <td style={bold}>{m.fat.pct}%</td>
            <td style={bold}>{m.fat.kcal}</td>
            <td style={bold}>{m.fat.gkg}</td>
          </tr>
        </tbody>
      </table>
      <div style={{ marginTop: "8px", textAlign: "right", fontSize: "13px", color: "var(--accent)", fontWeight: "600" }}>
        Total: {m.kcal_total} Kcal
      </div>
    </div>
  );
}

// ─── Formulário de alimento ───────────────────────────────────────────────────

const FOOD_CATEGORIES = [
  "Carnes e Aves",
  "Frios e Embutidos",
  "Peixes e Frutos do Mar",
  "Ovos",
  "Laticínios",
  "Cereais e Grãos",
  "Tubérculos e Raízes",
  "Frutas",
  "Leguminosas",
  "Vegetais e Verduras",
  "Oleaginosas e Sementes",
  "Óleos e Gorduras",
  "Bebidas e Cafés",
  "Doces e Adoçantes",
  "Condimentos e Temperos",
  "Suplementos",
  "Outros",
];

const EMPTY_FOOD = {
  name: "", category: "", protein_animal_g: "", protein_veg_g: "",
  carbs_g: "", fiber_g: "", fat_g: "", kcal: "", density_g_per_ml: "1",
  unit_weight_g: "", unit_name: "",
};

function FoodForm({ initial, onSave, onCancel }) {
  const sanitize = (obj) => Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, v ?? ""]));
  const [form, setForm] = useState(initial ? { ...EMPTY_FOOD, ...sanitize(initial) } : EMPTY_FOOD);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) return;
    onSave({
      name:             form.name.trim(),
      category:         form.category || null,
      protein_animal_g: parseFloat(form.protein_animal_g) || 0,
      protein_veg_g:    parseFloat(form.protein_veg_g)    || 0,
      carbs_g:          parseFloat(form.carbs_g)          || 0,
      fiber_g:          parseFloat(form.fiber_g)          || 0,
      fat_g:            parseFloat(form.fat_g)            || 0,
      kcal:             parseFloat(form.kcal)             || 0,
      density_g_per_ml: parseFloat(form.density_g_per_ml) || 1.0,
      unit_weight_g: form.unit_weight_g !== "" ? parseFloat(form.unit_weight_g) || null : null,
      unit_name:     form.unit_name?.trim() || null,
    });
  }

  const iCls = "w-full bg-[var(--border-0-10)] border border-[var(--border-0-15)] rounded px-2 py-1 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--blue)]";
  const lCls = "block text-xs text-[var(--text-secondary)] mb-1";

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <label className={lCls}>Nome do alimento *</label>
          <input className={iCls} value={form.name} onChange={set("name")} placeholder="ex: Frango grelhado" required />
        </div>
        <div>
          <label className={lCls}>Categoria</label>
          <select className={iCls} value={form.category} onChange={set("category")}>
            <option value="" style={SELECT_OPTION_STYLE}>- Sem categoria -</option>
            {FOOD_CATEGORIES.map((c) => <option key={c} value={c} style={SELECT_OPTION_STYLE}>{c}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <label className={lCls}>Proteína Animal (g/100g)</label>
          <input className={iCls} type="number" step="any" min="0" value={form.protein_animal_g} onChange={set("protein_animal_g")} placeholder="0" />
        </div>
        <div>
          <label className={lCls}>Proteína Vegetal (g/100g)</label>
          <input className={iCls} type="number" step="any" min="0" value={form.protein_veg_g} onChange={set("protein_veg_g")} placeholder="0" />
        </div>
        <div>
          <label className={lCls}>Carboidratos (g/100g)</label>
          <input className={iCls} type="number" step="any" min="0" value={form.carbs_g} onChange={set("carbs_g")} placeholder="0" />
        </div>
        <div>
          <label className={lCls}>Fibras (g/100g)</label>
          <input className={iCls} type="number" step="any" min="0" value={form.fiber_g} onChange={set("fiber_g")} placeholder="0" />
        </div>
        <div>
          <label className={lCls}>Gordura/Lipídios (g/100g)</label>
          <input className={iCls} type="number" step="any" min="0" value={form.fat_g} onChange={set("fat_g")} placeholder="0" />
        </div>
        <div>
          <label className={lCls}>Kcal (por 100g)</label>
          <input className={iCls} type="number" step="any" min="0" value={form.kcal} onChange={set("kcal")} placeholder="0" />
        </div>
        <div>
          <label className={lCls}>
            Densidade (g/ml)
            <span className="ml-1 text-[var(--text-0-60)] font-normal">- líquidos/pastosos</span>
          </label>
          <input className={iCls} type="number" step="any" min="0.1" value={form.density_g_per_ml} onChange={set("density_g_per_ml")} placeholder="1.0" />
        </div>
      </div>
      <p className="text-xs text-[var(--text-0-60)]">Densidade = 1,0 para sólidos. Leite ≈ 1,03 · Azeite ≈ 0,92 · Mel ≈ 1,40</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1 border-t border-[var(--border-0-10)]">
        <div>
          <label className={lCls}>
            Peso por unidade (g)
            <span className="ml-1 text-[var(--text-0-60)] font-normal">- opcional</span>
          </label>
          <input className={iCls} type="number" step="any" min="0" value={form.unit_weight_g} onChange={set("unit_weight_g")} placeholder="ex: 60" />
        </div>
        <div>
          <label className={lCls}>
            Nome da unidade
            <span className="ml-1 text-[var(--text-0-60)] font-normal">- opcional</span>
          </label>
          <input className={iCls} value={form.unit_name} onChange={set("unit_name")} placeholder='ex: unidade, fatia, colher' />
        </div>
      </div>
      <p className="text-xs text-[var(--text-0-60)]">Preencha para permitir informar quantidade em unidades. Ex: ovo = 60 g/unidade, fatia de pão = 25 g/fatia.</p>
      <div className="flex gap-2 pt-1">
        <button type="button" onClick={onCancel} className="flex-1 bg-[var(--surface-0-60)] text-[var(--text-0-70)] text-sm py-1.5 rounded hover:bg-[var(--surface-0-60)] transition">
          Cancelar
        </button>
        <button type="submit" className="flex-1 bg-[var(--blue)] text-[var(--blue-on)] text-sm font-semibold py-1.5 rounded hover:bg-[var(--blue-hover)] transition">
          <FontAwesomeIcon icon={faFloppyDisk} className="mr-1" /> Salvar
        </button>
      </div>
    </form>
  );
}

// ─── Helpers de agrupamento ───────────────────────────────────────────────────

function groupByCategory(foods) {
  const map = new Map();
  for (const f of foods) {
    const key = f.category || "Outros";
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(f);
  }
  // ordena categorias na ordem canônica; desconhecidas vão pro fim
  const order = FOOD_CATEGORIES;
  return [...map.entries()].sort(([a], [b]) => {
    const ia = order.indexOf(a);
    const ib = order.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
}

// ─── Aba: banco de alimentos ──────────────────────────────────────────────────

function FoodsTab({ foods, onFoodsChange, isManager }) {
  const [editingFood, setEditingFood] = useState(null);
  const [collapsed, setCollapsed]     = useState(new Set()); // categorias fechadas
  const [search, setSearch]           = useState("");

  const filtered = search.trim()
    ? foods.filter((f) => f.name.toLowerCase().includes(search.trim().toLowerCase()))
    : foods;

  const groups = groupByCategory(filtered);

  function toggleCategory(cat) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      return next;
    });
  }

  async function handleSave(data) {
    try {
      if (editingFood === "new") {
        const res = await api.post("/nutrition/foods", data);
        onFoodsChange([...foods, res.data]);
      } else {
        const res = await api.put(`/nutrition/foods/${editingFood.id}`, data);
        onFoodsChange(foods.map((f) => (f.id === res.data.id ? res.data : f)));
      }
      setEditingFood(null);
    } catch { /* silently */ }
  }

  async function handleDelete(food) {
    if (!window.confirm(`Remover "${food.name}"?`)) return;
    await api.delete(`/nutrition/foods/${food.id}`);
    onFoodsChange(foods.filter((f) => f.id !== food.id));
  }

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex items-center gap-2">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar alimento…"
          className="flex-1 bg-[var(--surface-secondary)] border border-[var(--border-0-15)] rounded px-3 py-1.5 text-sm text-[var(--text-primary)] placeholder-[var(--text-0-50)] focus:outline-none focus:border-[var(--blue)]"
        />
        {editingFood == null && (
          <button onClick={() => setEditingFood("new")} className="flex items-center gap-1 text-xs bg-[var(--blue)] text-[var(--blue-on)] px-3 py-1.5 rounded font-semibold hover:bg-[var(--blue-hover)] transition shrink-0">
            <FontAwesomeIcon icon={faPlus} /> Novo
          </button>
        )}
      </div>

      {/* Formulário de criação/edição */}
      {editingFood != null && (
        <div className="bg-[var(--surface-strong)] border border-[var(--border-0-15)] rounded-lg p-4">
          <p className="text-xs text-[var(--text-secondary)] mb-3">{editingFood === "new" ? "Novo alimento" : `Editar: ${editingFood.name}`}</p>
          <FoodForm initial={editingFood === "new" ? null : editingFood} onSave={handleSave} onCancel={() => setEditingFood(null)} />
        </div>
      )}

      {/* Lista agrupada */}
      <div className="space-y-1 max-h-[420px] overflow-y-auto pr-1">
        {filtered.length === 0 && (
          <p className="text-center text-[var(--text-secondary)] text-sm py-8">Nenhum alimento encontrado.</p>
        )}
        {groups.map(([cat, items]) => (
          <div key={cat}>
            {/* Cabeçalho da categoria */}
            <button
              onClick={() => toggleCategory(cat)}
              className="w-full flex items-center justify-between px-2 py-1.5 text-left hover:bg-[var(--border-0-08)] rounded transition"
            >
              <span className="text-xs font-semibold text-[var(--blue)] uppercase tracking-wider">{cat}</span>
              <span className="text-xs text-[var(--text-0-60)]">{collapsed.has(cat) ? `▶ ${items.length}` : `▼`}</span>
            </button>

            {/* Itens da categoria */}
            {!collapsed.has(cat) && (
              <div className="space-y-0.5 mb-1">
                {items.map((food) => (
                  <div key={food.id} className="flex items-center justify-between bg-[var(--surface-secondary)] border border-[var(--border-0-10)] rounded-lg px-3 py-2 ml-2 hover:border-[var(--border-0-15)] transition">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[var(--text-primary)] truncate">
                        {food.name}
                        {food.density_g_per_ml !== 1.0 && (
                          <span className="ml-2 text-[10px] text-blue-400 font-normal">{food.density_g_per_ml} g/ml</span>
                        )}
                      </p>
                      <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                        P:{(food.protein_animal_g + food.protein_veg_g).toFixed(1)}g &nbsp;C:{food.carbs_g}g &nbsp;Fb:{food.fiber_g ?? 0}g &nbsp;G:{food.fat_g}g &nbsp;- {food.kcal} kcal/100g
                      </p>
                    </div>
                    {(isManager || food.user_id) && (
                      <div className="flex gap-1 ml-2 shrink-0">
                        <button onClick={() => setEditingFood(food)} className="text-[var(--text-secondary)] hover:text-[var(--blue)] p-4 -m-3 transition">
                          <FontAwesomeIcon icon={faPenToSquare} size="sm" />
                        </button>
                        <button onClick={() => handleDelete(food)} className="text-[var(--text-secondary)] hover:text-red-400 p-4 -m-3 transition">
                          <FontAwesomeIcon icon={faTrashCan} size="sm" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Editor de dieta ──────────────────────────────────────────────────────────

// Popup nativo do <select> é renderizado pelo SO, fora do compositing da
// página — cores com alpha (var(--surface-solid) etc.) não aplicam e o popup
// cai pro branco padrão do SO. rgb(var(--surface-solid-rgb)) é 100% opaco e
// resolve isso (mesmo padrão de SelfInsightsModal.module.css: .weeksSelect option).
const SELECT_OPTION_STYLE = { backgroundColor: "rgb(var(--surface-solid-rgb))", color: "var(--text-primary)" };

// Mensagens rotativas durante a geração da dieta — mesmo padrão de GENERATING_MESSAGES em SelfWorkoutModal.jsx
const DIET_GENERATING_MESSAGES = [
  "Analisando suas metas nutricionais…",
  "Selecionando os melhores alimentos…",
  "Balanceando proteínas, carboidratos e gorduras…",
  "Distribuindo as calorias entre as refeições…",
  "Ajustando pelas suas preferências…",
  "Finalizando sua dieta…",
];

const MEAL_NAMES = ["Café da manhã", "Lanche 1", "Almoço", "Lanche 2", "Jantar", "Ceia"];

function DietEditor({ diet: initialDiet, foods, students, isManager, onSaved, onCancel, initialStudentId = null }) {
  const isNew = !initialDiet?.id;
  const [date, setDate]           = useState(initialDiet?.date || todayLocalISO());
  const [name, setName]           = useState(initialDiet?.name || "");
  const [goal, setGoal]           = useState(initialDiet?.goal || "");
  const [targetKcal, setTargetKcal] = useState(initialDiet?.target_kcal || "");
  const [weightKg, setWeightKg]   = useState(initialDiet?.weight_kg || "");
  const [notes, setNotes]         = useState(initialDiet?.notes || "");
  const [forUserId, setForUserId] = useState(initialDiet?.user_id || (initialStudentId ? String(initialStudentId) : ""));
  const [meals, setMeals] = useState(() => {
    if (!initialDiet?.meals?.length) {
      return MEAL_NAMES.map((n, i) => ({ id: null, name: n, order: i, items: [] }));
    }
    // Garante que todas as 6 refeições padrão existam - preenche as faltantes com lista vazia
    const existing = [...initialDiet.meals].sort((a, b) => a.order - b.order);
    const existingNames = new Set(existing.map(m => m.name));
    const merged = [...existing];
    MEAL_NAMES.forEach((n, i) => {
      if (!existingNames.has(n)) {
        merged.push({ id: null, name: n, order: i, items: [] });
      }
    });
    return merged.sort((a, b) => a.order - b.order);
  });
  const [saving, setSaving] = useState(false);
  const [addingItemMealIdx, setAddingItemMealIdx] = useState(null);
  const [suggestedKcal, setSuggestedKcal] = useState(null); // eslint-disable-line no-unused-vars
  const [tdeeData, setTdeeData] = useState(null); // { tdee, bmr, weight_kg, missing, formula_used, formula_label, formulas }
  const [bmrFormula, setBmrFormula] = useState(initialDiet?.bmr_formula || "");
  const [profilePatch, setProfilePatch] = useState({
    weight_kg: "", height_cm: "", birth_date: "", activity_level: "", gender: "",
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const toastEditor = useToast();
  const [itemFood, setItemFood]       = useState(null);
  const [itemQty, setItemQty]         = useState("100");
  const [itemUnit, setItemUnit]       = useState("g");
  // Substituto manual
  const [addingSubFor, setAddingSubFor] = useState(null); // { mealIdx, itemIdx }
  const [subFood, setSubFood]         = useState(null);
  const [subQty, setSubQty]           = useState("100");
  const [subUnit, setSubUnit]         = useState("g");
  const [subMatchBy, setSubMatchBy]   = useState("kcal"); // "protein"|"carbs"|"fat"|"kcal"
  // Sugestões automáticas de substituição
  const [visibleSuggestions, setVisibleSuggestions] = useState(new Set()); // Set de "mealIdx-itemIdx"

  // Auto-fill student's latest weight from body_progress
  useEffect(() => {
    if (!isManager || !forUserId) return;
    api.get(`/body-progress/student/${forUserId}`)
      .then((res) => {
        const latest = res.data?.[0];
        if (latest?.weight_kg && !weightKg) {
          setWeightKg(String(latest.weight_kg));
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forUserId]);

  // Busca TDEE via Mifflin-St Jeor ao abrir nova dieta (professor ou aluno)
  useEffect(() => {
    if (!isNew) return;
    const uid = forUserId || (() => {
      try { return JSON.parse(localStorage.getItem("user"))?.id; } catch { return null; }
    })();
    // Zera ao trocar de aluno
    setTdeeData(null);
    setSuggestedKcal(null);
    setTargetKcal("");
    setGoal("");
    setBmrFormula("");
    setProfilePatch({ weight_kg: "", height_cm: "", birth_date: "", activity_level: "", gender: "" });
    if (!uid) return;
    api.get(`/nutrition/tdee/${uid}`)
      .then(res => {
        setTdeeData(res.data);
        if (res.data.tdee) {
          setSuggestedKcal(res.data.tdee);
          setTargetKcal(String(res.data.tdee));
        }
        if (res.data.formula_used) setBmrFormula(res.data.formula_used);
        setProfilePatch({
          weight_kg:      res.data.weight_kg      ? String(res.data.weight_kg)   : "",
          height_cm:      res.data.height_cm      ? String(res.data.height_cm)   : "",
          birth_date:     "",
          activity_level: res.data.activity_level || "",
          gender:         res.data.gender         || "",
        });
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forUserId, isNew]);

  // Refaz o cálculo de GET forçando uma fórmula específica (professor troca no seletor).
  const handleFormulaChange = async (formulaKey) => {
    const uid = forUserId || (() => {
      try { return JSON.parse(localStorage.getItem("user"))?.id; } catch { return null; }
    })();
    if (!uid) return;
    setBmrFormula(formulaKey);
    try {
      const res = await api.get(`/nutrition/tdee/${uid}`, { params: { formula: formulaKey } });
      setTdeeData(res.data);
      if (res.data.tdee) setTargetKcal(String(res.data.tdee));
      if (res.data.formula_used) setBmrFormula(res.data.formula_used);
    } catch (_) { /* silently */ }
  };

  const handleSaveProfileEditor = async () => {
    const uid = forUserId || (() => {
      try { return JSON.parse(localStorage.getItem("user"))?.id; } catch { return null; }
    })();
    if (!uid) return;
    const missing = [];
    if (!profilePatch.gender)         missing.push("gênero");
    if (!profilePatch.birth_date)     missing.push("data de nascimento");
    if (!profilePatch.height_cm)      missing.push("altura");
    if (!profilePatch.weight_kg)      missing.push("peso");
    if (!profilePatch.activity_level) missing.push("nível de atividade");
    if (missing.length) { toastEditor.error(`Preencha: ${missing.join(", ")}`); return; }
    setSavingProfile(true);
    try {
      await api.patch(`/users/${uid}/profile`, {
        gender:         profilePatch.gender,
        birth_date:     profilePatch.birth_date,
        height_cm:      Number(profilePatch.height_cm),
        weight_kg:      Number(profilePatch.weight_kg),
        activity_level: profilePatch.activity_level,
      });
      const res = await api.get(`/nutrition/tdee/${uid}`);
      setTdeeData(res.data);
      if (res.data.tdee) {
        setSuggestedKcal(res.data.tdee);
        setTargetKcal(String(res.data.tdee));
      }
      if (res.data.formula_used) setBmrFormula(res.data.formula_used);
    } catch (e) {
      toastEditor.error(e?.response?.data?.detail || "Erro ao salvar perfil");
    } finally {
      setSavingProfile(false);
    }
  };

  function defaultUnit(food) {
    if (!food) return "g";
    if (food.unit_weight_g) return "un";
    if (food.density_g_per_ml !== 1.0) return "ml";
    return "g";
  }

  function handleFoodSelect(food) {
    setItemFood(food);
    const u = defaultUnit(food);
    setItemUnit(u);
    setItemQty(u === "un" ? "1" : "100");
  }

  // Calcula gramas do substituto equivalentes a um macro do item preferido
  function calcEquivQty(preferred, subFood, matchBy, targetUnit) {
    const prefQty = preferred.quantity_g || 100;
    const prefEffG =
      preferred.unit === "un"
        ? prefQty * (preferred.food.unit_weight_g || 100)
        : preferred.unit === "ml"
        ? prefQty * (preferred.food.density_g_per_ml || 1)
        : prefQty;
    let prefPer100, subPer100;
    if (matchBy === "carbs") {
      prefPer100 = preferred.food.carbs_g || 0;
      subPer100  = subFood.carbs_g || 0;
    } else if (matchBy === "fat") {
      prefPer100 = preferred.food.fat_g || 0;
      subPer100  = subFood.fat_g || 0;
    } else if (matchBy === "kcal") {
      prefPer100 = preferred.food.kcal || 0;
      subPer100  = subFood.kcal || 0;
    } else {
      prefPer100 = (preferred.food.protein_animal_g || 0) + (preferred.food.protein_veg_g || 0);
      subPer100  = (subFood.protein_animal_g || 0) + (subFood.protein_veg_g || 0);
    }
    if (prefPer100 <= 0 || subPer100 <= 0) return null;
    const equivG = ((prefEffG / 100) * prefPer100 / subPer100) * 100;
    if (targetUnit === "un" && subFood.unit_weight_g)
      return String(Math.round((equivG / subFood.unit_weight_g) * 10) / 10);
    if (targetUnit === "ml" && subFood.density_g_per_ml)
      return String(Math.round(equivG / subFood.density_g_per_ml));
    return String(Math.round(equivG));
  }

  function applyEquivQty(food, u, matchBy) {
    if (!food || !addingSubFor) return;
    const preferred = meals[addingSubFor.mealIdx]?.items[addingSubFor.itemIdx];
    if (!preferred?.food) return;
    const qty = calcEquivQty(preferred, food, matchBy, u);
    setSubQty(qty !== null ? qty : (u === "un" ? "1" : "100"));
  }

  function handleSubFoodSelect(food) {
    setSubFood(food);
    const u = defaultUnit(food);
    setSubUnit(u);
    applyEquivQty(food, u, subMatchBy);
  }

  function handleSubMatchByChange(newMatchBy) {
    setSubMatchBy(newMatchBy);
    if (!subFood) return;
    applyEquivQty(subFood, subUnit, newMatchBy);
  }

  // Pesos por objetivo da dieta
  const GOAL_WEIGHTS = {
    cutting:    { protein: 3, kcal: 2, carbs: 1,   fat: 1,   fiber: 1 },
    bulking:    { protein: 2, kcal: 1, carbs: 2,   fat: 0.5, fiber: 0 },
    manutencao: { protein: 2, kcal: 2, carbs: 1,   fat: 1,   fiber: 0.5 },
    saude:      { protein: 2, kcal: 1, carbs: 0.5, fat: 0.5, fiber: 3 },
  };
  const DEFAULT_WEIGHTS = { protein: 2, kcal: 2, carbs: 1, fat: 1, fiber: 0.5 };

  function calcSubScore(prefEffG, prefFood, subFood, equivG, w) {
    const get = (food, grams, field) => (grams / 100) * (food[field] || 0);
    const div = (a, b) => Math.abs(a - b) / Math.max(Math.max(a, b), 0.1);
    const pp = get(prefFood, prefEffG, "protein_animal_g") + get(prefFood, prefEffG, "protein_veg_g");
    const sp = get(subFood, equivG, "protein_animal_g") + get(subFood, equivG, "protein_veg_g");
    return (
      w.protein * div(pp, sp) +
      w.kcal    * div(get(prefFood, prefEffG, "kcal"),    get(subFood, equivG, "kcal")) +
      w.carbs   * div(get(prefFood, prefEffG, "carbs_g"), get(subFood, equivG, "carbs_g")) +
      w.fat     * div(get(prefFood, prefEffG, "fat_g"),   get(subFood, equivG, "fat_g")) +
      w.fiber   * div(get(prefFood, prefEffG, "fiber_g"), get(subFood, equivG, "fiber_g"))
    );
  }

  // Categorias excluídas como fonte ou candidato de sugestão
  const NO_SUGGESTION_CATEGORIES = new Set([
    "Outros", "Suplementos",
    // nomes reais das categorias no banco (alinhado ao backend):
    "Bebidas e Cafés", "Alimentos Preparados", "Doces e Adoçantes",
    "Frios e Embutidos", "Condimentos e Temperos",
    // variantes legadas:
    "Bebidas", "Temperos e Condimentos",
  ]);

  // Macro-grupos: substituto deve ser do MESMO grupo do alimento original.
  // Espelha a validação do backend (diet_agent.collect_diet_problems) - evita
  // sugerir fruta para tubérculo só porque o perfil de carboidrato é parecido.
  const MACRO_GROUP = {
    "Carnes e Aves":          "proteina",
    "Peixes e Frutos do Mar": "proteina",
    "Ovos":                   "proteina",
    "Cereais e Grãos":        "carboidrato",
    "Tubérculos e Raízes":    "carboidrato",
    "Leguminosas":            "carboidrato",
    "Frutas":                 "fruta",
    "Laticínios":             "laticinios",
    "Suplementos":            "laticinios",
    "Óleos e Gorduras":       "gordura",
    "Vegetais e Verduras":    "vegetal",
  };

  // Nome TACO sem o último segmento (preparo) - só para nomes com 3+ segmentos
  const tacoBaseName = (name) => {
    const parts = (name || "").split(",").map((s) => s.trim().toLowerCase());
    return parts.length >= 3 ? parts.slice(0, -1).join(", ") : "";
  };
  // Último segmento do nome TACO (preparo: cozida, grelhado, crua…)
  const tacoPrep = (name) => {
    const parts = (name || "").split(",").map((s) => s.trim().toLowerCase());
    return parts.length >= 2 ? parts[parts.length - 1] : "";
  };
  // Estado "pronto para consumo" - busca no NOME INTEIRO (custom foods e
  // pratos como omelete não têm a palavra-chave no último segmento TACO).
  const COOKED_RE = /cozid|grelhad|assad|frit|refogad|ensopad|milanesa|purê|omelete|mexid|poch[eê]|conserva|enlatad|defumad|curad/;
  const isReadyToEat = (name) => COOKED_RE.test((name || "").toLowerCase());
  // Categorias inerentemente prontas - isentas da regra de estado
  const READY_CATEGORIES = new Set(["Laticínios", "Frutas", "Suplementos", "Óleos e Gorduras"]);
  // Refeições leves: ovos ↔ laticínios/suplementos são trocas proteicas legítimas
  const BRIDGE_GROUPS = new Set(["proteina", "laticinios"]);
  // Primeiro segmento do nome TACO (fonte): "Frango, peito, …" → "frango"
  const tacoSource = (name) => (name || "").split(",")[0].trim().toLowerCase();
  const VARIETY_CATS = new Set(["Carnes e Aves", "Peixes e Frutos do Mar"]);

  // Categorias adequadas por tipo de refeição (espelha o backend)
  const LIGHT_MEALS = new Set(["Café da manhã", "Lanche 1", "Lanche 2", "Ceia", "Lanche da manhã", "Lanche da tarde"]);
  const MAIN_MEALS  = new Set(["Almoço", "Jantar"]);
  const LIGHT_MEAL_CATEGORIES = new Set([
    "Cereais e Grãos", "Frutas", "Laticínios", "Ovos",
    "Óleos e Gorduras", "Leguminosas", "Tubérculos e Raízes",
  ]);
  const MAIN_MEAL_CATEGORIES = new Set([
    "Carnes e Aves", "Peixes e Frutos do Mar", "Cereais e Grãos",
    "Tubérculos e Raízes", "Leguminosas", "Vegetais e Verduras",
    "Ovos", "Laticínios", "Óleos e Gorduras",
  ]);

  // Perfil de macros normalizado: vetor [prot, carbs, fat] onde soma = 1
  function macroProfile(food) {
    const prot  = (food.protein_animal_g || 0) + (food.protein_veg_g || 0);
    const carbs = food.carbs_g || 0;
    const fat   = food.fat_g   || 0;
    const total = prot + carbs + fat;
    if (total < 0.5) return null; // sem dados nutricionais suficientes
    return [prot / total, carbs / total, fat / total];
  }

  // Distância euclidiana entre dois perfis normalizados (0 = idêntico, √2 ≈ 1.41 = oposto)
  function profileDist(a, b) {
    return Math.sqrt((a[0]-b[0])**2 + (a[1]-b[1])**2 + (a[2]-b[2])**2);
  }

  // Calcula sugestões de substituição com base em perfil de macros
  function getSuggestions(item, existingSubs, mealName = "", maxCount = 5) {
    if (!item?.food) return [];
    if (NO_SUGGESTION_CATEGORIES.has(item.food.category)) return [];

    const prefProfile = macroProfile(item.food);
    if (!prefProfile) return []; // alimento sem dados nutricionais

    const usedIds = new Set([item.food.id, ...(existingSubs || []).map((s) => s.food?.id).filter(Boolean)]);

    const prefEffG = item.unit === "un"
      ? (item.quantity_g || 1) * (item.food.unit_weight_g || 100)
      : item.unit === "ml"
      ? (item.quantity_g || 100) * (item.food.density_g_per_ml || 1)
      : (item.quantity_g || 100);

    const prefKcalPer100 = item.food.kcal || 0;
    const w = GOAL_WEIGHTS[goal] || DEFAULT_WEIGHTS;
    const prefGroup = MACRO_GROUP[item.food.category] || null;
    const prefBase  = tacoBaseName(item.food.name);
    const prefPrep  = tacoPrep(item.food.name);
    const prefCooked = item.food.user_id == null && isReadyToEat(item.food.name);
    const prefSource = tacoSource(item.food.name);
    const mealAllowed = LIGHT_MEALS.has(mealName) ? LIGHT_MEAL_CATEGORIES
                      : MAIN_MEALS.has(mealName)  ? MAIN_MEAL_CATEGORIES
                      : null;

    // LIMIAR: perfil deve ser similar (dist < 0.55 → ~70% de similaridade mínima)
    const PROFILE_CUTOFF = 0.55;

    return foods
      .filter((f) => !usedIds.has(f.id) && !NO_SUGGESTION_CATEGORIES.has(f.category))
      .map((f) => {
        // Mesmo macro-grupo obrigatório: se o original tem grupo conhecido,
        // candidato de grupo diferente OU desconhecido é descartado
        // (gelatina "Doces" não substitui batata "Tubérculos").
        // Exceção: em refeições leves, ovos ↔ laticínios/suplementos.
        const subGroup = MACRO_GROUP[f.category] || null;
        if (prefGroup && subGroup !== prefGroup) {
          const bridgeOk = LIGHT_MEALS.has(mealName)
            && subGroup && BRIDGE_GROUPS.has(prefGroup) && BRIDGE_GROUPS.has(subGroup);
          if (!bridgeOk) return null;
        }
        // Categoria adequada à refeição (frango não é sugerido no café da manhã;
        // alimentos cadastrados pelo professor passam sempre)
        if (mealAllowed && f.user_id == null && !mealAllowed.has(f.category)) return null;
        // Prato pronto só aceita substituto também pronto (exclui farinhas, pós, crus);
        // custom do professor e categorias inerentemente prontas são isentos
        if (prefCooked && f.user_id == null && !READY_CATEGORIES.has(f.category) && !isReadyToEat(f.name)) return null;
        // Mesmo alimento com outro preparo não é substituto
        if (prefBase && tacoBaseName(f.name) === prefBase) return null;

        const subProfile = macroProfile(f);
        if (!subProfile) return null;

        const dist = profileDist(prefProfile, subProfile);
        if (dist > PROFILE_CUTOFF) return null; // perfil muito diferente - não é substituto

        const subKcalPer100 = f.kcal || 0;
        // Ancoragem calórica; fallback: mesma quantidade
        const equivG = prefKcalPer100 > 0 && subKcalPer100 > 0
          ? Math.round((prefEffG / 100) * prefKcalPer100 / subKcalPer100 * 100)
          : Math.round(prefEffG);

        const macroScore = calcSubScore(prefEffG, item.food, f, equivG, w);
        // Mesma categoria → bônus forte (mandioca antes de arroz para batata)
        const catBonus = f.category === item.food.category ? -0.3 : 0;
        // Mesmo modo de preparo (cozida→cozida, grelhado→grelhado) → bônus
        const prepBonus = prefPrep && tacoPrep(f.name) === prefPrep ? -0.15 : 0;
        // Carnes/peixes: mesma fonte animal (frango→frango) rankeia depois
        // de fontes diferentes - variedade real primeiro
        const sourcePenalty = VARIETY_CATS.has(item.food.category) && tacoSource(f.name) === prefSource ? 0.25 : 0;
        // Quantidade equivalente absurda (ex: 600g para trocar 120g) → penaliza
        const qtyRatio = equivG / Math.max(prefEffG, 1);
        const qtyPenalty = Math.min(0.5, Math.abs(Math.log10(Math.max(qtyRatio, 0.01))) * 0.4);
        // Score final: perfil de macros é o fator dominante
        const score = dist * 2.5 + macroScore * 0.3 + catBonus + prepBonus + sourcePenalty + qtyPenalty;

        const u = f.unit_weight_g ? "un" : (f.density_g_per_ml && f.density_g_per_ml !== 1.0) ? "ml" : "g";
        let dispQty = equivG;
        if (u === "un" && f.unit_weight_g) dispQty = Math.round((equivG / f.unit_weight_g) * 10) / 10;
        else if (u === "ml" && f.density_g_per_ml && f.density_g_per_ml !== 1.0) dispQty = Math.round(equivG / f.density_g_per_ml);
        return { food: f, unit: u, quantity_g: equivG, dispQty: dispQty || equivG, score, profileDist: dist };
      })
      .filter(Boolean)
      .sort((a, b) => a.score - b.score)
      .slice(0, maxCount);
  }

  function addItem(mealIdx) {
    if (!itemFood) return;
    setMeals((prev) =>
      prev.map((m, i) =>
        i !== mealIdx ? m : {
          ...m,
          items: [...m.items, { food_id: itemFood.id, food: itemFood, quantity_g: parseFloat(itemQty) || 100, unit: itemUnit, substitutes: [] }],
        }
      )
    );
    // Mostra sugestões automáticas se o alimento tem categoria
    if (itemFood.category) {
      const newItemIdx = meals[mealIdx].items.length;
      setVisibleSuggestions((prev) => new Set([...prev, `${mealIdx}-${newItemIdx}`]));
    }
    setItemFood(null);
    setItemQty("100");
    setItemUnit("g");
    // mantém formulário aberto para adicionar próximo alimento rapidamente
  }

  function dismissSuggestions(mealIdx, itemIdx) {
    setVisibleSuggestions((prev) => {
      const next = new Set(prev);
      next.delete(`${mealIdx}-${itemIdx}`);
      return next;
    });
  }

  function addSuggestionAsSubstitute(mealIdx, itemIdx, suggestion) {
    setMeals((prev) =>
      prev.map((m, i) =>
        i !== mealIdx ? m : {
          ...m,
          items: m.items.map((it, j) =>
            j !== itemIdx ? it : {
              ...it,
              substitutes: [...(it.substitutes || []), {
                food_id: suggestion.food.id,
                food: suggestion.food,
                quantity_g: suggestion.quantity_g,
                unit: suggestion.unit,
              }],
            }
          ),
        }
      )
    );
  }

  function addSubstitute(mealIdx, itemIdx) {
    if (!subFood) return;
    setMeals((prev) =>
      prev.map((m, i) =>
        i !== mealIdx ? m : {
          ...m,
          items: m.items.map((it, j) =>
            j !== itemIdx ? it : {
              ...it,
              substitutes: [...(it.substitutes || []), { food_id: subFood.id, food: subFood, quantity_g: parseFloat(subQty) || 100, unit: subUnit }],
            }
          ),
        }
      )
    );
    setSubFood(null);
    setSubQty("100");
    setSubUnit("g");
    // mantém formulário aberto para adicionar mais substitutos
  }

  function removeSubstitute(mealIdx, itemIdx, subIdx) {
    setMeals((prev) =>
      prev.map((m, i) =>
        i !== mealIdx ? m : {
          ...m,
          items: m.items.map((it, j) =>
            j !== itemIdx ? it : {
              ...it,
              substitutes: (it.substitutes || []).filter((_, k) => k !== subIdx),
            }
          ),
        }
      )
    );
  }

  function removeItem(mealIdx, itemIdx) {
    setMeals((prev) =>
      prev.map((m, i) =>
        i !== mealIdx ? m : { ...m, items: m.items.filter((_, j) => j !== itemIdx) }
      )
    );
  }

  function updateItem(mealIdx, itemIdx, patch) {
    setMeals((prev) =>
      prev.map((m, i) =>
        i !== mealIdx ? m : {
          ...m,
          items: m.items.map((it, j) => {
            if (j !== itemIdx) return it;
            const updated = { ...it, ...patch };
            // Se a quantidade do principal mudou, escala os substitutos proporcionalmente
            if ("quantity_g" in patch && it.quantity_g > 0 && patch.quantity_g !== it.quantity_g) {
              const ratio = patch.quantity_g / it.quantity_g;
              updated.substitutes = (it.substitutes || []).map((s) => ({
                ...s,
                quantity_g: Math.round(s.quantity_g * ratio * 10) / 10,
              }));
            }
            return updated;
          }),
        }
      )
    );
  }

  function updateSubstitute(mealIdx, itemIdx, subIdx, patch) {
    setMeals((prev) =>
      prev.map((m, i) =>
        i !== mealIdx ? m : {
          ...m,
          items: m.items.map((it, j) =>
            j !== itemIdx ? it : {
              ...it,
              substitutes: (it.substitutes || []).map((s, k) => k !== subIdx ? s : { ...s, ...patch }),
            }
          ),
        }
      )
    );
  }

  async function handleSave() {
    setSaving(true);
    const payload = {
      date,
      name: name || null,
      goal: goal || null,
      target_kcal: parseFloat(targetKcal) || null,
      weight_kg: parseFloat(weightKg) || null,
      notes: notes || null,
      for_user_id: isManager && forUserId ? Number(forUserId) : null,
      bmr_formula: bmrFormula || null,
      meals: meals.map((m, i) => ({
        name: m.name,
        order: i,
        items: m.items.map((it) => ({
          food_id:    it.food_id ?? it.food?.id,
          quantity_g: it.quantity_g,
          unit:       it.unit || "g",
          substitutes: (it.substitutes || []).map((s) => ({
            food_id:    s.food_id ?? s.food?.id,
            quantity_g: s.quantity_g,
            unit:       s.unit || "g",
          })),
        })),
      })),
    };
    try {
      const res = isNew
        ? await api.post("/nutrition/diets", payload)
        : await api.put(`/nutrition/diets/${initialDiet.id}`, payload);
      onSaved(res.data);
    } catch (e) {
      toastEditor.error(e?.response?.data?.detail || "Erro ao salvar dieta. Tente novamente.");
    }
    setSaving(false);
  }

  const iCls = "bg-[var(--border-0-10)] border border-[var(--border-0-15)] rounded px-2 py-1 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--blue)] w-full";

  return (
    <div className="space-y-4">
      {/* Barra de macros sticky */}
      <MacroBar meals={meals} weightKg={parseFloat(weightKg) || null} targetKcal={parseFloat(targetKcal) || null} />

      {/* Seletor de aluno - visível só para professor/admin */}
      {isManager && students.length > 0 && (
        <div>
          <label className="block text-xs text-[var(--text-secondary)] mb-1">
            <FontAwesomeIcon icon={faUser} className="mr-1" />
            Aluno (deixe em branco para criar para si mesmo)
          </label>
          <select
            value={forUserId}
            onChange={(e) => setForUserId(e.target.value)}
            className={iCls}
            disabled={!isNew}
          >
            <option value="" style={SELECT_OPTION_STYLE}>- Para mim mesmo -</option>
            {students.map((s) => (
              <option key={s.id} value={s.id} style={SELECT_OPTION_STYLE}>{s.name} {s.email ? `(${s.email})` : ""}</option>
            ))}
          </select>
          {!isNew && (
            <p className="text-xs text-[var(--text-0-60)] mt-1">Aluno não pode ser alterado após criação.</p>
          )}
        </div>
      )}

      {/* Campos da dieta */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div style={{ gridColumn: "1 / -1" }}>
          <label className="block text-xs text-[var(--text-secondary)] mb-1">Data</label>
          <SplitDateInput value={date} onChange={setDate} inputClassName={iCls} />
        </div>
        <div>
          <label className="block text-xs text-[var(--text-secondary)] mb-1">Peso corporal (kg)</label>
          <input type="number" step="any" min="0" className={iCls} value={weightKg} onChange={(e) => setWeightKg(e.target.value)} placeholder="ex: 70" />
        </div>
        <div>
          <label className="block text-xs text-[var(--text-secondary)] mb-1">Objetivo</label>
          <select className={iCls} value={goal} onChange={(e) => {
            const g = e.target.value;
            const tdee = tdeeData?.tdee;
            const GOAL_FACTOR = { manutencao: 1.0, cutting: 0.82, bulking: 1.12, saude: 0.90 };
            if (tdee && GOAL_FACTOR[g]) setTargetKcal(String(Math.round(tdee * GOAL_FACTOR[g])));
            setGoal(g);
          }}>
            <option value="" style={SELECT_OPTION_STYLE}>- Selecione -</option>
            <option value="cutting" style={SELECT_OPTION_STYLE}>Cutting (perda de gordura)</option>
            <option value="manutencao" style={SELECT_OPTION_STYLE}>Manutenção</option>
            <option value="bulking" style={SELECT_OPTION_STYLE}>Bulking (ganho de massa)</option>
            <option value="saude" style={SELECT_OPTION_STYLE}>Saúde geral</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-[var(--text-secondary)] mb-1 flex items-center gap-2">
            Meta calórica (kcal/dia)
            {tdeeData?.tdee && (
              <span style={{
                fontSize: "var(--fs-xs)", padding: "2px 7px", borderRadius: 10,
                background: "rgba(99,202,183,0.12)", border: "1px solid rgba(99,202,183,0.3)",
                color: "var(--accent)", fontWeight: 600,
              }}>
                <FontAwesomeIcon icon={faFire} /> GET {tdeeData.tdee} kcal · {tdeeData.formula_label || "Mifflin-St Jeor"}
              </span>
            )}
            {isNew && tdeeData?.missing?.length > 0 && !tdeeData?.tdee && (
              <span style={{ fontSize: "var(--fs-xs)", color: "var(--feedback-warning)", opacity: 0.8 }}>
                <FontAwesomeIcon icon={faTriangleExclamation} /> perfil incompleto
              </span>
            )}
          </label>
          <input type="number" step="any" min="0" className={iCls} value={targetKcal} onChange={(e) => setTargetKcal(e.target.value)} placeholder="ex: 2200" />
          {isManager && isNew && tdeeData?.formulas?.length > 0 && (
            <div style={{ marginTop: 6 }}>
              <select
                value={bmrFormula || tdeeData.formula_used || ""}
                onChange={(e) => handleFormulaChange(e.target.value)}
                className={iCls}
                style={{ fontSize: "var(--fs-xs)", padding: "4px 6px" }}
              >
                {tdeeData.formulas.map((f) => (
                  <option
                    key={f.key}
                    value={f.key}
                    disabled={!f.available}
                    title={!f.available ? f.reason : undefined}
                    style={SELECT_OPTION_STYLE}
                  >
                    {f.label}{f.recommended ? " (recomendada)" : ""}{!f.available ? " - indisponível" : ""}
                  </option>
                ))}
              </select>
              {tdeeData.formula_reason && (
                <p style={{ margin: "4px 0 0", fontSize: "var(--fs-xs)", color: "var(--text-0-40)", lineHeight: 1.4 }}>
                  {tdeeData.formula_reason}
                </p>
              )}
            </div>
          )}
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs text-[var(--text-secondary)] mb-1">Nome da dieta (opcional)</label>
          <input className={iCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="ex: Dieta de treino" />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs text-[var(--text-secondary)] mb-1">Observações (opcional)</label>
          <input className={iCls} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="ex: Dia de descanso, aumentar proteína..." />
        </div>
      </div>

      {/* Painel de perfil incompleto - só em nova dieta */}
      {isNew && tdeeData !== null && tdeeData?.missing?.length > 0 && !tdeeData?.tdee && (
        <div style={{
          background: "rgba(245,158,11,0.07)", border: "1px solid rgba(245,158,11,0.25)",
          borderRadius: 10, padding: "14px 16px", marginBottom: 4,
        }}>
          <p style={{ margin: "0 0 10px", fontSize: 12, color: "var(--feedback-warning)", fontWeight: 600 }}>
            <FontAwesomeIcon icon={faTriangleExclamation} /> Complete o perfil para calcular o GET automaticamente
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {/* Gênero */}
            <div style={{ gridColumn: "1 / -1" }}>
              <label className="block text-xs text-[var(--text-secondary)] mb-1">Gênero</label>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {[["masculino","Masculino"],["feminino","Feminino"],["outro","Outro"]].map(([v,l]) => (
                  <button key={v} type="button"
                    onClick={() => setProfilePatch(p => ({ ...p, gender: v }))}
                    style={{
                      padding: "5px 12px", borderRadius: 16, fontSize: 12, cursor: "pointer",
                      border: profilePatch.gender === v ? "1px solid var(--accent)" : "1px solid var(--border-0-10)",
                      background: profilePatch.gender === v ? "rgba(99,202,183,0.15)" : "var(--border-0-04)",
                      color: profilePatch.gender === v ? "var(--accent)" : "var(--text-0-70)",
                      fontWeight: profilePatch.gender === v ? 700 : 400,
                    }}
                  >{l}</button>
                ))}
              </div>
            </div>
            {/* Data de nascimento */}
            <div style={{ gridColumn: "1 / -1" }}>
              <label className="block text-xs text-[var(--text-secondary)] mb-1">Data de nascimento</label>
              <SplitDateInput
                value={profilePatch.birth_date}
                onChange={v => setProfilePatch(p => ({ ...p, birth_date: v }))}
                inputClassName={iCls}
                maxDate={todayLocalISO()}
              />
            </div>
            {/* Altura */}
            <div>
              <label className="block text-xs text-[var(--text-secondary)] mb-1">Altura (cm)</label>
              <input type="number" className={iCls} placeholder="Ex: 175" min={50} max={250} step="any"
                value={profilePatch.height_cm}
                onChange={e => setProfilePatch(p => ({ ...p, height_cm: e.target.value }))}
              />
            </div>
            {/* Peso */}
            <div>
              <label className="block text-xs text-[var(--text-secondary)] mb-1">Peso (kg)</label>
              <input type="number" className={iCls} placeholder="Ex: 75" min={30} max={300} step="any"
                value={profilePatch.weight_kg}
                onChange={e => setProfilePatch(p => ({ ...p, weight_kg: e.target.value }))}
              />
            </div>
            {/* Nível de atividade */}
            <div style={{ gridColumn: "1 / -1" }}>
              <label className="block text-xs text-[var(--text-secondary)] mb-1">Nível de atividade</label>
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                {[
                  ["sedentario",  "Sedentário",          "Pouco ou nenhum exercício"],
                  ["leve",        "Levemente ativo",     "1–3×/semana"],
                  ["moderado",    "Moderadamente ativo", "3–5×/semana"],
                  ["ativo",       "Muito ativo",         "6–7×/semana"],
                  ["muito_ativo", "Extremamente ativo",  "2×/dia ou trabalho físico"],
                ].map(([v, l, d]) => (
                  <button key={v} type="button"
                    onClick={() => setProfilePatch(p => ({ ...p, activity_level: v }))}
                    style={{
                      display: "flex", alignItems: "baseline", gap: 8,
                      padding: "7px 12px", borderRadius: 8, textAlign: "left", cursor: "pointer",
                      border: profilePatch.activity_level === v ? "1px solid var(--accent)" : "1px solid var(--border-0-10)",
                      background: profilePatch.activity_level === v ? "rgba(99,202,183,0.12)" : "rgba(255,255,255,0.04)",
                      color: profilePatch.activity_level === v ? "var(--accent)" : "var(--text-0-70)",
                    }}
                  >
                    <span style={{ fontSize: 12, fontWeight: 600 }}>{l}</span>
                    <span style={{ fontSize: "var(--fs-xs)", opacity: 0.6 }}>{d}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={handleSaveProfileEditor}
            disabled={savingProfile}
            style={{
              marginTop: 12, padding: "7px 18px", borderRadius: 8, border: "none",
              background: "var(--accent)", color: "var(--on-accent)", fontSize: 13, fontWeight: 700, cursor: "pointer",
              opacity: savingProfile ? 0.6 : 1,
            }}
          >
            {savingProfile ? "Salvando..." : "Salvar e calcular GET"}
          </button>
        </div>
      )}

      {/* Tabela de macros em tempo real */}
      <div>
        <p className="text-xs text-[var(--text-secondary)] mb-2 font-semibold uppercase tracking-wide">Resumo de Macros</p>
        <MacroTable meals={meals} weightKg={parseFloat(weightKg) || null} />
      </div>

      {/* Refeições */}
      <div className="space-y-3">
        {meals.map((meal, mealIdx) => (
          <div key={mealIdx} className="bg-[var(--surface-secondary)] border border-[var(--border-0-10)] rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 bg-[var(--surface-strong)] border-b border-[var(--border-0-10)]">
              <span className="text-sm font-semibold text-[var(--blue)]">
                <FontAwesomeIcon icon={faUtensils} className="mr-2 text-xs" />
                {meal.name}
              </span>
              <button
                onClick={() => {
                  setAddingItemMealIdx(mealIdx);
                  setItemFood(null);
                  setItemQty("100");
                  setItemUnit("g");
                }}
                className="text-xs text-[var(--text-secondary)] hover:text-[var(--blue)] transition flex items-center gap-1"
              >
                <FontAwesomeIcon icon={faPlus} /> Alimento
              </button>
            </div>

            <div className="divide-y divide-[var(--border-0-10)]">
              {meal.items.length === 0 && (
                <p className="text-xs text-[var(--text-0-60)] px-3 py-2">Nenhum alimento nesta refeição</p>
              )}
              {meal.items.map((item, itemIdx) => (
                <div key={itemIdx} className="border-b border-[var(--border-0-10)] last:border-0">
                  {/* Item preferido */}
                  <div className="flex items-center gap-2 px-3 py-1.5">
                    <span className="flex-1 text-sm text-[var(--text-0-70)] truncate">{item.food?.name}</span>
                    <input
                      type="number" min="1" step="any"
                      value={item.quantity_g}
                      onChange={(e) => updateItem(mealIdx, itemIdx, { quantity_g: parseFloat(e.target.value) || 0 })}
                      className="w-16 bg-[var(--surface-strong)] border border-[var(--border-0-15)] rounded px-1.5 py-0.5 text-xs text-[var(--text-primary)] text-center focus:outline-none focus:border-[var(--blue)]"
                    />
                    <select
                      value={item.unit || "g"}
                      onChange={(e) => updateItem(mealIdx, itemIdx, { unit: e.target.value })}
                      className="bg-[var(--surface-strong)] border border-[var(--border-0-15)] rounded px-1 py-0.5 text-xs text-[var(--text-0-70)] focus:outline-none focus:border-[var(--blue)]"
                    >
                      <option value="g" style={SELECT_OPTION_STYLE}>g</option>
                      <option value="ml" style={SELECT_OPTION_STYLE}>ml</option>
                      {item.food?.unit_weight_g && <option value="un" style={SELECT_OPTION_STYLE}>un</option>}
                    </select>
                    <button
                      onClick={() => { dismissSuggestions(mealIdx, itemIdx); setAddingSubFor({ mealIdx, itemIdx }); setSubFood(null); setSubQty("100"); setSubUnit("g"); setSubMatchBy("kcal"); }}
                      title="Adicionar substituto"
                      className="text-[var(--text-secondary)] hover:text-[var(--blue)] transition p-0.5"
                    >
                      <FontAwesomeIcon icon={faRightLeft} size="xs" />
                    </button>
                    <button onClick={() => removeItem(mealIdx, itemIdx)} className="text-[var(--text-0-60)] hover:text-red-400 transition p-0.5">
                      <FontAwesomeIcon icon={faXmark} size="sm" />
                    </button>
                  </div>

                  {/* Substitutos */}
                  {(item.substitutes || []).map((sub, subIdx) => (
                    <div key={subIdx} className="flex items-center gap-2 pl-7 pr-3 py-1 bg-[var(--surface-strong)]">
                      <span className="text-[10px] text-[var(--blue)] mr-1 whitespace-nowrap">Sub {subIdx + 1}</span>
                      <span className="flex-1 text-xs text-[var(--text-secondary)] truncate">{sub.food?.name}</span>
                      <input
                        type="number" min="1" step="any"
                        value={sub.quantity_g}
                        onChange={(e) => updateSubstitute(mealIdx, itemIdx, subIdx, { quantity_g: parseFloat(e.target.value) || 0 })}
                        className="w-14 bg-[var(--surface-strong)] border border-[var(--border-0-10)] rounded px-1.5 py-0.5 text-xs text-[var(--text-primary)] text-center focus:outline-none focus:border-[var(--blue)]"
                      />
                      <select
                        value={sub.unit || "g"}
                        onChange={(e) => updateSubstitute(mealIdx, itemIdx, subIdx, { unit: e.target.value })}
                        className="bg-[var(--surface-strong)] border border-[var(--border-0-10)] rounded px-1 py-0.5 text-xs text-[var(--text-secondary)] focus:outline-none focus:border-[var(--blue)]"
                      >
                        <option value="g" style={SELECT_OPTION_STYLE}>g</option>
                        <option value="ml" style={SELECT_OPTION_STYLE}>ml</option>
                        {sub.food?.unit_weight_g && <option value="un" style={SELECT_OPTION_STYLE}>un</option>}
                      </select>
                      <button onClick={() => removeSubstitute(mealIdx, itemIdx, subIdx)} className="text-[var(--text-0-60)] hover:text-red-400 transition p-0.5">
                        <FontAwesomeIcon icon={faXmark} size="xs" />
                      </button>
                    </div>
                  ))}

                  {/* Sugestões automáticas de substituição */}
                  {visibleSuggestions.has(`${mealIdx}-${itemIdx}`) && (() => {
                    const suggestions = getSuggestions(item, item.substitutes, meal.name);
                    if (suggestions.length === 0) return null;
                    const GOAL_LABEL = { cutting: "Cutting", bulking: "Bulking", manutencao: "Manutenção", saude: "Saúde" };
                    // Score final (perfil + categoria + preparo + quantidade) → 0-100%.
                    // score ≤ 0 (match ideal com bônus) = 100%; 1.5 = 0%.
                    const fitPct = (s) => Math.max(0, Math.min(100, Math.round((1 - Math.max(0, s.score ?? 0) / 1.5) * 100)));
                    const fitColor = (pct) => pct >= 75 ? "var(--accent)" : pct >= 45 ? "var(--feedback-warning)" : "var(--feedback-error)";
                    return (
                      <div style={{ background: "var(--surface-primary)", borderTop: "1px solid var(--border-0-10)" }} className="px-3 py-2.5">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-bold text-[var(--blue)] uppercase tracking-wider">
                              <FontAwesomeIcon icon={faLightbulb} /> Substituições
                            </span>
                            {goal && (
                              <span style={{ background: "rgba(var(--accent-rgb),0.15)", border: "1px solid rgba(var(--accent-rgb),0.3)", borderRadius: 4, padding: "1px 6px", fontSize: "var(--fs-xs)", color: "var(--accent)", fontWeight: 700 }}>
                                {GOAL_LABEL[goal] || goal}
                              </span>
                            )}
                          </div>
                          <button onClick={() => dismissSuggestions(mealIdx, itemIdx)}
                            className="text-[10px] text-[var(--text-0-60)] hover:text-[var(--text-secondary)] transition">
                            dispensar ×
                          </button>
                        </div>
                        <div className="flex flex-col gap-1.5">
                          {suggestions.map((sug) => {
                            const alreadyAdded = (item.substitutes || []).some((s) => s.food?.id === sug.food.id);
                            const pct = fitPct(sug);
                            const color = fitColor(pct);
                            return (
                              <button
                                key={sug.food.id}
                                disabled={alreadyAdded}
                                onClick={() => addSuggestionAsSubstitute(mealIdx, itemIdx, sug)}
                                style={{
                                  display: "flex", alignItems: "center", gap: 8,
                                  padding: "6px 10px", borderRadius: 8, textAlign: "left",
                                  background: alreadyAdded ? "rgba(var(--accent-rgb),0.08)" : "rgba(255,255,255,0.03)",
                                  border: `1px solid ${alreadyAdded ? "rgba(var(--accent-rgb),0.3)" : "var(--border-0-06)"}`,
                                  cursor: alreadyAdded ? "default" : "pointer",
                                  transition: "all 0.15s",
                                }}
                                onMouseEnter={(e) => { if (!alreadyAdded) e.currentTarget.style.background = "rgba(var(--accent-rgb),0.10)"; }}
                                onMouseLeave={(e) => { if (!alreadyAdded) e.currentTarget.style.background = "rgba(255,255,255,0.03)"; }}
                              >
                                {/* Barra de fit */}
                                <div style={{ width: 3, height: 28, borderRadius: 2, background: alreadyAdded ? "var(--blue)" : color, flexShrink: 0 }} />
                                {/* Nome + quantidade */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 12, color: alreadyAdded ? "var(--blue)" : "var(--text-primary)", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {alreadyAdded ? <><FontAwesomeIcon icon={faCheck} /> </> : ""}{sug.food.name}
                                  </div>
                                  <div style={{ fontSize: "var(--fs-xs)", color: "var(--text-secondary)", marginTop: 1 }}>
                                    {sug.dispQty}{sug.unit}
                                    {sug.food.kcal > 0 && ` · ${Math.round((sug.quantity_g / 100) * sug.food.kcal)} kcal`}
                                  </div>
                                </div>
                                {/* Score badge */}
                                {!alreadyAdded && (
                                  <div style={{ fontSize: "var(--fs-xs)", fontWeight: 700, color, flexShrink: 0 }}>
                                    {pct}%
                                  </div>
                                )}
                              </button>
                            );
                          })}
                          <button
                            onClick={() => {
                              dismissSuggestions(mealIdx, itemIdx);
                              setAddingSubFor({ mealIdx, itemIdx });
                              setSubFood(null); setSubQty("100"); setSubUnit("g"); setSubMatchBy("kcal");
                            }}
                            style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 10px", borderRadius: 8, fontSize: "var(--fs-xs)", color: "var(--text-secondary)", border: "1px dashed #333", background: "transparent", cursor: "pointer" }}
                          >
                            + buscar outro alimento
                          </button>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Formulário de adicionar substituto */}
                  {addingSubFor?.mealIdx === mealIdx && addingSubFor?.itemIdx === itemIdx && (
                    <div className="flex flex-col gap-1.5 pl-7 pr-3 py-2 bg-[var(--surface-strong)] border-t border-[var(--border-0-10)]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] text-[var(--blue)] whitespace-nowrap">Equivaler por:</span>
                        {[
                          { key: "protein", label: "Prot" },
                          { key: "carbs",   label: "Carb" },
                          { key: "fat",     label: "Lip"  },
                          { key: "kcal",    label: "Kcal" },
                        ].map(({ key, label }) => (
                          <button key={key} type="button" onClick={() => handleSubMatchByChange(key)}
                            className={`text-[10px] px-2 py-0.5 rounded transition ${subMatchBy === key ? "bg-[var(--blue)] text-[var(--blue-on)] font-semibold" : "bg-[var(--surface-secondary)] text-[var(--text-secondary)] border border-[var(--border-0-15)] hover:text-[var(--text-primary)]"}`}
                          >{label}</button>
                        ))}
                      </div>
                      <FoodPicker
                        foods={foods}
                        onSelect={handleSubFoodSelect}
                        placeholder="Buscar substituto…"
                        autoFocus
                      />
                      {subFood && (
                        <div className="flex flex-wrap gap-2 mt-1">
                          <span className="text-[10px] text-[var(--blue)] flex-1 truncate self-center">{subFood.name}</span>
                          <input type="number" min="1" value={subQty} onChange={(e) => setSubQty(e.target.value)}
                            className="w-14 bg-[var(--surface-secondary)] border border-[var(--border-0-15)] rounded px-2 py-1 text-xs text-[var(--text-primary)] text-center focus:outline-none focus:border-[var(--blue)]"
                            placeholder="qtd"
                          />
                          <select value={subUnit} onChange={(e) => setSubUnit(e.target.value)}
                            className="bg-[var(--surface-secondary)] border border-[var(--border-0-15)] rounded px-2 py-1 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[var(--blue)]"
                          >
                            <option value="g" style={SELECT_OPTION_STYLE}>g</option>
                            <option value="ml" style={SELECT_OPTION_STYLE}>ml</option>
                            {subFood?.unit_weight_g && <option value="un" style={SELECT_OPTION_STYLE}>un</option>}
                          </select>
                          <button onClick={() => addSubstitute(mealIdx, itemIdx)} className="bg-[var(--blue)] text-[var(--blue-on)] text-xs font-semibold px-2 py-1 rounded hover:bg-[var(--blue-hover)] transition">OK</button>
                        </div>
                      )}
                      <button onClick={() => { setAddingSubFor(null); setSubFood(null); }} className="text-[10px] text-[var(--text-secondary)] hover:text-[var(--text-0-70)] transition mt-1">
                        cancelar
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Adicionar item */}
            {addingItemMealIdx === mealIdx && (
              <div className="px-3 py-2 bg-[var(--surface-strong)] border-t border-[var(--border-0-10)] space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wide">+ alimento · Enter para confirmar</span>
                  <button onClick={() => { setAddingItemMealIdx(null); setItemFood(null); }} className="text-[10px] text-[var(--text-secondary)] hover:text-[var(--text-0-70)] transition">fechar ×</button>
                </div>
                <FoodPicker
                  foods={foods}
                  onSelect={handleFoodSelect}
                  placeholder="Buscar alimento…"
                  autoFocus
                />
                {itemFood && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[var(--blue)] flex-1 truncate">{itemFood.name}</span>
                    <input
                      type="number"
                      min="1"
                      value={itemQty}
                      onChange={(e) => setItemQty(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") addItem(mealIdx); }}
                      autoFocus
                      className="w-16 bg-[var(--surface-secondary)] border border-[var(--border-0-15)] rounded px-2 py-1 text-sm text-[var(--text-primary)] text-center focus:outline-none focus:border-[var(--blue)]"
                      placeholder="qtd"
                    />
                    <select
                      value={itemUnit}
                      onChange={(e) => setItemUnit(e.target.value)}
                      className="bg-[var(--surface-secondary)] border border-[var(--border-0-15)] rounded px-2 py-1 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--blue)]"
                    >
                      <option value="g" style={SELECT_OPTION_STYLE}>g</option>
                      <option value="ml" style={SELECT_OPTION_STYLE}>ml</option>
                      {itemFood?.unit_weight_g && <option value="un" style={SELECT_OPTION_STYLE}>un</option>}
                    </select>
                    <button onClick={() => addItem(mealIdx)} className="bg-[var(--blue)] text-[var(--blue-on)] text-xs font-semibold px-3 py-1 rounded hover:bg-[var(--blue-hover)] transition">
                      ↵ OK
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="flex gap-2 pt-2">
        <button onClick={onCancel} className="flex-1 bg-[var(--surface-0-60)] text-[var(--text-0-70)] py-2 rounded hover:bg-[var(--surface-0-60)] transition text-sm">
          Cancelar
        </button>
        <button onClick={handleSave} disabled={saving} className="flex-1 bg-[var(--blue)] text-[var(--blue-on)] font-semibold py-2 rounded hover:bg-[var(--blue-hover)] transition text-sm disabled:opacity-50">
          <FontAwesomeIcon icon={faFloppyDisk} className="mr-1" />
          {saving ? "Salvando…" : "Salvar dieta"}
        </button>
      </div>
    </div>
  );
}

// ─── Aba: lista de dietas ─────────────────────────────────────────────────────

function DietsTab({ foods, students, isManager, isAluno, canCreateOwnDiet = false, currentUserId, initialStudentId = null, onGeneratingChange, onClose }) {
  const [diets, setDiets]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [view, setView]               = useState("list");
  const [selectedDiet, setSelectedDiet] = useState(null);
  const [filterUserId, setFilterUserId] = useState(initialStudentId ? String(initialStudentId) : "");
  const [selectedSubs, setSelectedSubs] = useState({}); // eslint-disable-line no-unused-vars
  // Clone
  const [cloningDiet, setCloningDiet] = useState(null); // diet being cloned
  const [cloneDate, setCloneDate]     = useState("");
  const [cloneUserId, setCloneUserId] = useState("");
  const [cloning, setCloning]         = useState(false);
  // AI Diet
  const [aiForm, setAiForm] = useState({
    student_id: initialStudentId || "",
    kcal_target: "",
    meal_names: ["Café da manhã", "Almoço", "Jantar", "Ceia"],
    goal: "",
    preferences: "",
    restrictions: "",
    protein_pct: 30,
    carb_pct: 40,
    fat_pct: 30,
  });
  const toast = useToast();
  const [pdfLoading, setPdfLoading]   = useState(false);

  // PDF da dieta com a marca do professor (gerado no backend; só professor/admin)
  const downloadDietPdf = async (diet) => {
    if (pdfLoading) return;
    setPdfLoading(true);
    try {
      await downloadPdf(api, `/nutrition/diets/${diet.id}/pdf`, `dieta-${diet.date}.pdf`);
    } catch (e) {
      toast.error("Não foi possível gerar o PDF da dieta. Tente novamente.");
    } finally {
      setPdfLoading(false);
    }
  };
  const [aiLoading, setAiLoading]     = useState(false);
  const [checkingAiLimit, setCheckingAiLimit] = useState(false); // pré-checagem do limite mensal
  const [genMsgIdx, setGenMsgIdx]     = useState(0); // frase rotativa durante a geração
  const [aiMealPreviews, setAiMealPreviews] = useState([]); // refeições chegando via SSE
  const [aiError, setAiError]         = useState(""); // eslint-disable-line no-unused-vars
  const [aiDraft, setAiDraft]         = useState(null);      // GenerateDietResponse
  const [showAiConfirm, setShowAiConfirm] = useState(false); // sub-view de confirmação
  const [aiSavedDiet, setAiSavedDiet] = useState(null);      // dieta após auto-save
  // TDEE
  const [aiTdee, setAiTdee]           = useState(null); // { tdee, bmr, weight_kg, missing, formula_used, formula_label, formulas }
  const [aiBmrFormula, setAiBmrFormula] = useState("");
  // Friction delete
  const [confirmDeleteDiet, setConfirmDeleteDiet] = useState(null); // diet object pendente de exclusão
  const [deleting, setDeleting]       = useState(false);

  // Busca TDEE ao mudar o aluno selecionado no form de IA
  useEffect(() => {
    // Manager precisa ter aluno selecionado; aluno usa o próprio ID
    const uid = isManager ? Number(aiForm.student_id) : currentUserId;
    if (!uid) { setAiTdee(null); return; }
    // Zera campos do perfil ao trocar de aluno
    setProfilePatch({ weight_kg: "", height_cm: "", birth_date: "", activity_level: "", gender: "" });
    setAiTdee(null);
    setAiBmrFormula("");
    api.get(`/nutrition/tdee/${uid}`)
      .then(res => {
        setAiTdee(res.data);
        if (res.data.tdee) {
          setAiForm(f => ({ ...f, kcal_target: res.data.tdee }));
        }
        if (res.data.formula_used) setAiBmrFormula(res.data.formula_used);
        // Pré-preenche com o que já existe no perfil
        setProfilePatch({
          weight_kg:      res.data.weight_kg      ? String(res.data.weight_kg)   : "",
          height_cm:      res.data.height_cm      ? String(res.data.height_cm)   : "",
          birth_date:     "",
          activity_level: res.data.activity_level || "",
          gender:         res.data.gender         || "",
        });
      })
      .catch(() => setAiTdee(null));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiForm.student_id, currentUserId]);

  // Refaz o cálculo de GET forçando uma fórmula específica (professor troca no seletor).
  const handleAiFormulaChange = async (formulaKey) => {
    const uid = isManager ? Number(aiForm.student_id) : currentUserId;
    if (!uid) return;
    setAiBmrFormula(formulaKey);
    try {
      const res = await api.get(`/nutrition/tdee/${uid}`, { params: { formula: formulaKey } });
      setAiTdee(res.data);
      if (res.data.tdee) setAiForm(f => ({ ...f, kcal_target: res.data.tdee }));
      if (res.data.formula_used) setAiBmrFormula(res.data.formula_used);
    } catch (_) { /* silently */ }
  };

  // Rotaciona mensagens durante a geração da dieta (mesmo padrão de treino, ver SelfWorkoutModal)
  useEffect(() => {
    if (!aiLoading) return;
    setGenMsgIdx(0);
    const iv = setInterval(() => setGenMsgIdx(i => Math.min(i + 1, DIET_GENERATING_MESSAGES.length - 1)), 5000);
    return () => clearInterval(iv);
  }, [aiLoading]);

  const [profilePatch, setProfilePatch] = useState({
    weight_kg: "", height_cm: "", birth_date: "", activity_level: "", gender: "",
  });
  const [savingProfile, setSavingProfile] = useState(false);

  const handleSaveProfile = async () => {
    const uid = Number(aiForm.student_id) || currentUserId;
    if (!uid) return;
    const missing = [];
    if (!profilePatch.gender)         missing.push("gênero");
    if (!profilePatch.birth_date)     missing.push("data de nascimento");
    if (!profilePatch.height_cm)      missing.push("altura");
    if (!profilePatch.weight_kg)      missing.push("peso");
    if (!profilePatch.activity_level) missing.push("nível de atividade");
    if (missing.length) {
      toast.error(`Preencha: ${missing.join(", ")}`);
      return;
    }
    const body = {
      gender:         profilePatch.gender,
      birth_date:     profilePatch.birth_date,
      height_cm:      Number(profilePatch.height_cm),
      weight_kg:      Number(profilePatch.weight_kg),
      activity_level: profilePatch.activity_level,
    };
    setSavingProfile(true);
    try {
      await api.patch(`/users/${uid}/profile`, body);
      // Rebusca TDEE
      const res = await api.get(`/nutrition/tdee/${uid}`);
      setAiTdee(res.data);
      if (res.data.tdee) setAiForm(f => ({ ...f, kcal_target: res.data.tdee }));
      if (res.data.formula_used) setAiBmrFormula(res.data.formula_used);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar perfil");
    } finally {
      setSavingProfile(false);
    }
  };

  const load = useCallback(async (uid) => {
    setLoading(true);
    try {
      const params = uid ? `?user_id=${uid}` : "";
      const res = await api.get(`/nutrition/diets${params}`);
      setDiets(res.data);
    } catch { /* silently */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    load(filterUserId);
  }, [load, filterUserId]);

  // Aluno vai direto para o detalhe da dieta mais recente — só na primeira
  // vez que a lista carrega (abertura do modal). Sem o guard de ref, esse
  // efeito disparava de novo toda vez que "view" virava "list" — inclusive
  // quando o próprio usuário clicava em "← Outras" pra voltar (ex: pra gerar
  // outra dieta), fazendo o clique "piscar" e voltar direto pro detalhe.
  const autoRedirectedRef = useRef(false);
  useEffect(() => {
    if (isAluno && diets.length > 0 && view === "list" && !autoRedirectedRef.current) {
      autoRedirectedRef.current = true;
      setSelectedDiet(diets[0]);
      setView("detail");
    }
  }, [isAluno, diets, view]);

  function handleSaved(diet) {
    setDiets((prev) => {
      const exists = prev.find((d) => d.id === diet.id);
      return exists ? prev.map((d) => (d.id === diet.id ? diet : d)) : [diet, ...prev];
    });
    setView("list");
  }

  function handleDelete(diet) {
    setConfirmDeleteDiet(diet);
  }

  async function executeDeleteDiet(diet) {
    setDeleting(true);
    try {
      await api.delete(`/nutrition/diets/${diet.id}`);
      setDiets((prev) => prev.filter((d) => d.id !== diet.id));
      setConfirmDeleteDiet(null);
    } catch {
      alert("Erro ao excluir dieta.");
    } finally {
      setDeleting(false);
    }
  }

  function openClone(diet) {
    setCloningDiet(diet);
    setCloneDate(todayLocalISO());
    setCloneUserId(isManager ? String(diet.user_id || currentUserId) : "");
  }

  async function handleClone() {
    if (!cloningDiet || !cloneDate) return;
    setCloning(true);
    try {
      // Busca a dieta completa caso não tenha meals carregadas
      let src = cloningDiet;
      if (!src.meals) {
        const r = await api.get(`/nutrition/diets/${src.id}`);
        src = r.data;
      }
      const payload = {
        date: cloneDate,
        name: src.name ? `${src.name} (cópia)` : null,
        goal: src.goal,
        target_kcal: src.target_kcal,
        weight_kg: src.weight_kg,
        notes: src.notes,
        for_user_id: cloneUserId ? Number(cloneUserId) : undefined,
        meals: (src.meals || []).map((m) => ({
          name: m.name,
          order: m.order,
          items: (m.items || []).map((it) => ({
            food_id: it.food_id || it.food?.id,
            quantity_g: it.quantity_g,
            unit: it.unit || "g",
            substitutes: (it.substitutes || []).map((s) => ({
              food_id: s.food_id || s.food?.id,
              quantity_g: s.quantity_g,
              unit: s.unit || "g",
            })),
          })),
        })),
      };
      const res = await api.post("/nutrition/diets", payload);
      setDiets((prev) => [res.data, ...prev]);
      setCloningDiet(null);
    } catch (e) {
      alert("Erro ao duplicar dieta.");
    } finally {
      setCloning(false);
    }
  }

  function ownerLabel(diet) {
    if (!isManager || diet.user_id === currentUserId) return null;
    const s = students.find((s) => s.id === diet.user_id);
    return s ? s.name : `Aluno #${diet.user_id}`;
  }

  // ── AI Diet helpers ──────────────────────────────────────────────────────

  // Espelha no cliente o guard-rail fisiológico do backend (app/api/ai_diet.py,
  // "M9: Validação fisiológica de proteína") — mesma faixa e mesma fórmula de
  // sugestão, pra avisar o aluno antes de ele preencher o resto do formulário
  // e só descobrir depois do 422 num toast que já sumiu da tela.
  function getProteinValidation() {
    const weightKg = Number(profilePatch.weight_kg) || aiTdee?.weight_kg || null;
    const kcalTarget = Number(aiForm.kcal_target) || 0;
    if (!weightKg || weightKg <= 0 || kcalTarget <= 0) {
      return { weightKg, kcalTarget, gPerKg: null, outOfRange: false, suggestedPct: null, suggestedG: null };
    }
    const proteinG = Math.round(kcalTarget * Number(aiForm.protein_pct) / 100 / 4);
    const gPerKg = proteinG / weightKg;
    const outOfRange = gPerKg < 1.0 || gPerKg > 3.0;
    const suggestedPct = Math.round(1.6 * weightKg * 4 * 100 / kcalTarget);
    const suggestedG = Math.round(1.6 * weightKg);
    return { weightKg, kcalTarget, gPerKg, outOfRange, suggestedPct, suggestedG };
  }

  // Abre o pop-up de confirmação antes de chamar a IA
  function handleGenerateAiDiet(e) {
    e.preventDefault();
    setAiError("");
    if (!aiForm.goal) {
      toast.error("Selecione o objetivo antes de gerar a dieta.");
      return;
    }
    // GET (Mifflin-St Jeor) exige perfil completo — sem isso, a meta calórica
    // digitada não tem base real nos dados do aluno. Bloqueia mesmo que o
    // campo "Meta calórica" já tenha algum valor (auto-preenchido de uma
    // seleção anterior, ou digitado manualmente).
    if (aiTdee?.missing?.length > 0) {
      toast.error(`Complete o perfil antes de gerar a dieta: ${aiTdee.missing.join(", ")}.`);
      return;
    }
    const proteinCheck = getProteinValidation();
    if (proteinCheck.outOfRange) {
      toast.error(`Proteína ${proteinCheck.gPerKg.toFixed(2)} g/kg fora do fisiológico (1.0–3.0 g/kg). Ajuste o percentual ou use "Aplicar sugestão".`);
      return;
    }
    setShowAiConfirm(true);
  }

  // Geração + auto-save em sequência (chamado após o usuário confirmar)
  async function handleGenerateAndSaveAiDiet() {
    setShowAiConfirm(false);
    setAiDraft(null);
    setAiSavedDiet(null);
    setAiMealPreviews([]);
    setAiLoading(true);
    onGeneratingChange?.(true);
    try {
      const payload = {
        ...aiForm,
        student_id: Number(aiForm.student_id) || currentUserId,
        kcal_target: Number(aiForm.kcal_target),
        n_meals: aiForm.meal_names.length,
        meal_names: aiForm.meal_names,
        protein_pct: Number(aiForm.protein_pct),
        carb_pct: Number(aiForm.carb_pct),
        fat_pct: Number(aiForm.fat_pct),
        // aiForm não carrega peso/altura — sem isso o backend nunca recebia
        // esses dados (nem pra gravar na dieta, nem pro contexto corporal do
        // prompt), mesmo com o perfil já preenchido pra calcular o GET.
        weight_kg: Number(profilePatch.weight_kg) || aiTdee?.weight_kg || null,
        height_cm: Number(profilePatch.height_cm) || aiTdee?.height_cm || null,
      };
      // 1. Gera a dieta (chamada síncrona - streaming removido)
      const genRes = await api.post("/ai/generate-diet", payload);
      const draft = genRes.data;
      if (!draft) throw new Error("Nenhuma resposta recebida da IA.");
      setAiDraft(draft);
      // 2. Salva automaticamente
      const today = todayLocalISO();
      const saveBody = {
        date: today,
        name: draft.name,
        goal: draft.goal,
        target_kcal: draft.target_kcal,
        weight_kg: draft.weight_kg || null,
        ai_generated: true,
        ai_usage_log_id: draft.ai_usage_log_id ?? null,
        bmr_formula: aiBmrFormula || null,
        for_user_id: draft.for_user_id !== currentUserId ? draft.for_user_id : undefined,
        meals: (() => {
          // Garante que todas as 6 refeições padrão sejam salvas (mesmo as vazias)
          const draftByName = Object.fromEntries(draft.meals.map((m) => [m.name, m]));
          return MEAL_NAMES.map((name, i) => {
            const m = draftByName[name];
            return {
              name,
              order: i,
              items: m ? m.items.map((it) => ({
                food_id: it.food_id,
                quantity_g: it.quantity_g,
                unit: it.unit,
                substitutes: (it.substitutes || []).map((s) => ({
                  food_id: s.food_id,
                  quantity_g: s.quantity_g,
                  unit: s.unit,
                })),
              })) : [],
            };
          });
        })(),
      };
      const saveRes = await api.post("/nutrition/diets", saveBody);
      const saved = saveRes.data;
      setAiSavedDiet(saved);
      setDiets(prev => {
        const exists = prev.find(d => d.id === saved.id);
        return exists ? prev.map(d => d.id === saved.id ? saved : d) : [saved, ...prev];
      });
      setView("ai-preview");
    } catch (err) {
      const detail = err?.detail || err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao gerar dieta. Tente novamente.");
    } finally {
      setAiLoading(false);
      setAiMealPreviews([]);
      onGeneratingChange?.(false);
    }
  }

  if (view === "new") {
    return <DietEditor foods={foods} students={students} isManager={isManager} onSaved={handleSaved} onCancel={() => setView("list")} initialStudentId={initialStudentId} />;
  }

  // ── View: form IA ──────────────────────────────────────────────────────────
  if (view === "ai") {
    const AI_GOAL_LABELS = { cutting: "Cutting", manutencao: "Manutenção", bulking: "Bulking", saude: "Saúde" };
    const totalPct = Number(aiForm.protein_pct) + Number(aiForm.carb_pct) + Number(aiForm.fat_pct);
    const macroOk  = totalPct === 100;
    // GET (Mifflin-St Jeor) exige perfil completo — sem isso a meta calórica
    // não tem base real. Bloqueia o submit tanto visualmente (disabled) quanto
    // no handler (defesa dupla, ver handleGenerateAiDiet).
    const profileIncomplete = aiTdee?.missing?.length > 0;
    const proteinCheck = getProteinValidation();
    const formInvalid = !aiForm.goal || profileIncomplete;
    const inputCls = "w-full bg-[var(--border-0-10)] border border-[var(--border-0-10)] rounded-lg px-3 py-2 min-h-[44px] sm:min-h-0 text-base sm:text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--blue)]";
    const labelCls = "block text-xs text-[var(--text-secondary)] mb-1";
    const sectionCls = "text-[10px] font-bold uppercase tracking-widest text-[var(--text-secondary)] pt-1";

    // ── Sub-view de geração (tela cheia, mesmo padrão de SelfWorkoutModal) ──
    if (aiLoading) {
      return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 18, padding: "48px 24px", textAlign: "center" }}>
          <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: 36, color: "var(--blue)" }} />
          <p key={genMsgIdx} style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "var(--text-primary)", lineHeight: 1.5, animation: "gen-msg-in-diet 0.35s cubic-bezier(0.22,1,0.36,1) both" }}>
            {DIET_GENERATING_MESSAGES[genMsgIdx]}
          </p>
          <p style={{ margin: 0, fontSize: 13, color: "var(--text-0-40)" }}>Personalizando cada refeição para você</p>
          <style>{`@keyframes gen-msg-in-diet { from { opacity: 0; transform: translateY(7px); } to { opacity: 1; transform: translateY(0); } }`}</style>
        </div>
      );
    }

    // ── Sub-view de confirmação ────────────────────────────────────────────
    if (showAiConfirm) {
      const studentObj  = students.find(s => String(s.id) === String(aiForm.student_id));
      const studentName = aiForm.student_id && String(aiForm.student_id) !== String(currentUserId)
        ? (studentObj?.name || `Aluno #${aiForm.student_id}`)
        : "Minha dieta";
      const SRow = ({ label, value }) => (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8, fontSize: 13 }}>
          <span style={{ color: "var(--text-secondary)", flexShrink: 0, marginRight: 12 }}>{label}</span>
          <span style={{ color: "var(--text-primary)", fontWeight: 500, textAlign: "right" }}>{value}</span>
        </div>
      );
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <button onClick={() => setShowAiConfirm(false)} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition text-sm shrink-0 min-h-[44px] sm:min-h-0 flex items-center">← Editar parâmetros</button>
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faWandMagicSparkles} className="text-[var(--blue)] text-sm" />
              <h3 className="text-sm font-bold text-[var(--text-primary)]">Confirmar geração</h3>
            </div>
          </div>

          {/* Resumo dos parâmetros */}
          <div style={{ background: "var(--surface-primary)", borderRadius: 12, padding: 16, border: "1px solid var(--border-0-10)" }}>
            <SRow label="Aluno" value={studentName} />
            <SRow label="Objetivo" value={AI_GOAL_LABELS[aiForm.goal] || aiForm.goal} />
            <SRow label="Meta calórica" value={`${Number(aiForm.kcal_target).toLocaleString("pt-BR")} kcal/dia`} />
            <SRow label="Refeições" value={`${aiForm.meal_names.length} - ${aiForm.meal_names.join(" · ")}`} />
            <SRow label="Distribuição" value={`Prot ${aiForm.protein_pct}% · Carb ${aiForm.carb_pct}% · Gord ${aiForm.fat_pct}%`} />

            {aiForm.preferences.trim() && <SRow label="Preferências" value={<><FontAwesomeIcon icon={faCheck} /> Preenchidas</>} />}
            {aiForm.restrictions.trim() && <SRow label="Restrições" value={<><FontAwesomeIcon icon={faCheck} /> Preenchidas</>} />}
          </div>

          {/* Aviso de crédito */}
          <div style={{ background: "rgba(251,191,36,0.07)", border: "1px solid rgba(251,191,36,0.2)", borderRadius: 10, padding: "12px 14px" }}>
            <div style={{ color: "var(--feedback-warning)", fontWeight: 700, fontSize: 12, marginBottom: 3 }}><FontAwesomeIcon icon={faBolt} /> Crédito de IA será consumido</div>
            <div style={{ color: "var(--feedback-warning)", fontSize: 12 }}>Revise os parâmetros acima antes de confirmar. Cada geração tem custo - evite regenerações desnecessárias.</div>
          </div>

          {/* Info sobre edição pós-save */}
          <div style={{ background: "rgba(var(--accent-rgb),0.05)", border: "1px solid rgba(var(--accent-rgb),0.12)", borderRadius: 10, padding: "12px 14px", fontSize: 12, color: "var(--feedback-success)" }}>
            <FontAwesomeIcon icon={faPenToSquare} /> A dieta será <strong style={{ color: "var(--accent)" }}>salva automaticamente</strong> ao ser gerada. Você poderá editar cada item manualmente ou excluí-la se preferir recomeçar.
          </div>



          <div className="flex gap-2">
            <button
              onClick={() => setShowAiConfirm(false)}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 min-h-[44px] sm:min-h-0 rounded-xl text-sm font-semibold"
              style={{ background: "var(--surface-0-60)", color: "var(--text-secondary)", border: "1px solid var(--border-0-10)" }}
            >
              Cancelar
            </button>
            <button
              onClick={handleGenerateAndSaveAiDiet}
              disabled={aiLoading}
              className="flex-[2] flex items-center justify-center gap-2 py-2.5 min-h-[44px] sm:min-h-0 rounded-xl text-sm font-bold transition"
              style={{
                background: aiLoading ? "rgba(var(--accent-rgb),0.12)" : "var(--accent)",
                color: aiLoading ? "var(--text-0-40)" : "var(--text-primary)",
                cursor: aiLoading ? "not-allowed" : "pointer",
              }}
            >
              {aiLoading
                ? <><FontAwesomeIcon icon={faSpinner} spin /> {aiMealPreviews.length > 0 ? `Montando ${aiMealPreviews[aiMealPreviews.length - 1]}…` : "Gerando dieta…"}</>
                : <><FontAwesomeIcon icon={faWandMagicSparkles} /> Confirmar e gerar</>}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <button onClick={() => setView("list")} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition text-sm shrink-0 min-h-[44px] sm:min-h-0 flex items-center">← Voltar</button>
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faWandMagicSparkles} className="text-[var(--blue)] text-sm" />
            <h3 className="text-sm font-bold text-[var(--text-primary)]">Nova dieta com IA</h3>
          </div>
        </div>

        <form onSubmit={handleGenerateAiDiet} className="space-y-4">
          {/* Aluno */}
          {isManager && students.length > 0 && (
            <div>
              <label className={labelCls}>Aluno</label>
              <select
                value={aiForm.student_id}
                onChange={e => setAiForm(f => ({ ...f, student_id: e.target.value }))}
                className={inputCls}
                required
              >
                <option value="" style={SELECT_OPTION_STYLE}>Selecione um aluno</option>
                <option value={currentUserId} style={SELECT_OPTION_STYLE}>Minha dieta</option>
                {students.map(s => <option key={s.id} value={s.id} style={SELECT_OPTION_STYLE}>{s.name}</option>)}
              </select>
            </div>
          )}

          {/* Cooldown warning */}
          {(() => {
            const forUserId = Number(aiForm.student_id) || currentUserId;
            const now = Date.now();
            const thirtyDays = 30 * 24 * 60 * 60 * 1000;
            const recentAiDiet = diets.find(d =>
              d.ai_generated &&
              d.user_id === forUserId &&
              (now - new Date(d.created_at).getTime()) < thirtyDays
            );
            if (!recentAiDiet) return null;
            const daysAgo = Math.floor((now - new Date(recentAiDiet.created_at).getTime()) / (24 * 60 * 60 * 1000));
            const daysLabel = daysAgo === 0 ? "hoje" : daysAgo === 1 ? "ontem" : `há ${daysAgo} dias`;
            return (
              <div style={{ background: "rgba(234,179,8,0.08)", border: "1px solid rgba(234,179,8,0.25)", borderRadius: 10, padding: "10px 14px", fontSize: 12, color: "var(--feedback-warning)" }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}><FontAwesomeIcon icon={faTriangleExclamation} /> Dieta gerada por IA recentemente ({daysLabel})</div>
                <div style={{ color: "var(--feedback-warning)", marginBottom: 8 }}>
                  Editar a dieta existente é mais eficiente e econômico do que gerar uma nova.
                </div>
                <button
                  type="button"
                  onClick={() => { setSelectedDiet(recentAiDiet); setView("edit"); }}
                  className="min-h-[44px] sm:min-h-0"
                  style={{ background: "rgba(234,179,8,0.15)", border: "1px solid rgba(234,179,8,0.3)", color: "var(--feedback-warning)", borderRadius: 6, padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                >
                  <FontAwesomeIcon icon={faPenToSquare} /> Editar dieta existente
                </button>
              </div>
            );
          })()}

          <div className={sectionCls}>Metas</div>

          {/* Objetivo + Calorias */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Objetivo</label>
              <select value={aiForm.goal} onChange={e => {
                const goal = e.target.value;
                const tdee = aiTdee?.tdee;
                const GOAL_FACTOR = { manutencao: 1.0, cutting: 0.82, bulking: 1.12, saude: 0.90 };
                const kcal = tdee && GOAL_FACTOR[goal] ? Math.round(tdee * GOAL_FACTOR[goal]) : "";
                setAiForm(f => ({ ...f, goal, kcal_target: kcal }));
              }} className={inputCls} required>
                <option value="" style={SELECT_OPTION_STYLE}>- Selecione -</option>
                <option value="manutencao" style={SELECT_OPTION_STYLE}>Manutenção</option>
                <option value="cutting" style={SELECT_OPTION_STYLE}>Cutting</option>
                <option value="bulking" style={SELECT_OPTION_STYLE}>Bulking</option>
                <option value="saude" style={SELECT_OPTION_STYLE}>Saúde</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Meta calórica (kcal)</label>
              <input
                type="number" min={800} max={6000} step="any" inputMode="decimal"
                value={aiForm.kcal_target}
                onChange={e => setAiForm(f => ({ ...f, kcal_target: e.target.value }))}
                className={inputCls} required
              />
              {aiTdee?.tdee && (
                <span style={{
                  display: "inline-block", marginTop: 6,
                  fontSize: "var(--fs-xs)", padding: "2px 7px", borderRadius: 10,
                  background: "rgba(99,202,183,0.12)", border: "1px solid rgba(99,202,183,0.3)",
                  color: "var(--accent)", fontWeight: 600, letterSpacing: "0.01em",
                }}>
                  <FontAwesomeIcon icon={faFire} /> GET {aiTdee.tdee} kcal · {aiTdee.formula_label || "Mifflin-St Jeor"}
                </span>
              )}
              {aiTdee?.missing?.length > 0 && !aiTdee?.tdee && (
                <span style={{ display: "inline-block", marginTop: 6, fontSize: "var(--fs-xs)", color: "var(--feedback-warning)" }}>
                  <FontAwesomeIcon icon={faTriangleExclamation} /> perfil incompleto
                </span>
              )}
              {isManager && aiTdee?.formulas?.length > 0 && (
                <div style={{ marginTop: 6 }}>
                  <select
                    value={aiBmrFormula || aiTdee.formula_used || ""}
                    onChange={(e) => handleAiFormulaChange(e.target.value)}
                    className={inputCls}
                    style={{ fontSize: "var(--fs-xs)", padding: "4px 6px" }}
                  >
                    {aiTdee.formulas.map((f) => (
                      <option
                        key={f.key}
                        value={f.key}
                        disabled={!f.available}
                        title={!f.available ? f.reason : undefined}
                        style={SELECT_OPTION_STYLE}
                      >
                        {f.label}{f.recommended ? " (recomendada)" : ""}{!f.available ? " - indisponível" : ""}
                      </option>
                    ))}
                  </select>
                  {aiTdee.formula_reason && (
                    <p style={{ margin: "4px 0 0", fontSize: "var(--fs-xs)", color: "var(--text-0-40)", lineHeight: 1.4 }}>
                      {aiTdee.formula_reason}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Painel de perfil incompleto */}
          {aiTdee !== null && aiTdee?.missing?.length > 0 && !aiTdee?.tdee && (
            <div style={{
              background: "rgba(245,158,11,0.07)", border: "1px solid rgba(245,158,11,0.25)",
              borderRadius: 10, padding: "14px 16px", marginBottom: 12,
            }}>
              <p style={{ margin: "0 0 10px", fontSize: 12, color: "var(--feedback-warning)", fontWeight: 600 }}>
                <FontAwesomeIcon icon={faTriangleExclamation} /> Complete o perfil para calcular o GET automaticamente
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {/* Gênero */}
                <div style={{ gridColumn: "1 / -1" }}>
                  <label className={labelCls}>Gênero</label>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {[["masculino","Masculino"],["feminino","Feminino"],["outro","Outro"]].map(([v,l]) => (
                      <button key={v} type="button"
                        onClick={() => setProfilePatch(p => ({ ...p, gender: v }))}
                        className="min-h-[44px] sm:min-h-0"
                        style={{
                          padding: "5px 12px", borderRadius: 16, fontSize: 12, cursor: "pointer",
                          border: profilePatch.gender === v ? "1px solid var(--accent)" : "1px solid var(--border-0-10)",
                          background: profilePatch.gender === v ? "rgba(99,202,183,0.15)" : "var(--border-0-04)",
                          color: profilePatch.gender === v ? "var(--accent)" : "var(--text-0-70)",
                          fontWeight: profilePatch.gender === v ? 700 : 400,
                        }}
                      >{l}</button>
                    ))}
                  </div>
                </div>
                {/* Data de nascimento */}
                <div>
                  <label className={labelCls}>Data de nascimento</label>
                  <SplitDateInput
                    value={profilePatch.birth_date}
                    onChange={v => setProfilePatch(p => ({ ...p, birth_date: v }))}
                    inputClassName={inputCls}
                    maxDate={todayLocalISO()}
                  />
                </div>
                {/* Altura */}
                <div>
                  <label className={labelCls}>Altura (cm)</label>
                  <input type="number" inputMode="numeric" className={inputCls} placeholder="Ex: 175" min={50} max={250}
                    value={profilePatch.height_cm}
                    onChange={e => setProfilePatch(p => ({ ...p, height_cm: e.target.value }))}
                  />
                </div>
                {/* Peso */}
                <div>
                  <label className={labelCls}>Peso (kg)</label>
                  <input type="number" inputMode="decimal" className={inputCls} placeholder="Ex: 75" min={30} max={300} step="any"
                    value={profilePatch.weight_kg}
                    onChange={e => setProfilePatch(p => ({ ...p, weight_kg: e.target.value }))}
                  />
                </div>
                {/* Nível de atividade */}
                <div style={{ gridColumn: "1 / -1" }}>
                  <label className={labelCls}>Nível de atividade</label>
                  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                    {[
                      ["sedentario",  "Sedentário",          "Pouco ou nenhum exercício"],
                      ["leve",        "Levemente ativo",     "1–3×/semana"],
                      ["moderado",    "Moderadamente ativo", "3–5×/semana"],
                      ["ativo",       "Muito ativo",         "6–7×/semana"],
                      ["muito_ativo", "Extremamente ativo",  "2×/dia ou trabalho físico"],
                    ].map(([v, l, d]) => (
                      <button key={v} type="button"
                        onClick={() => setProfilePatch(p => ({ ...p, activity_level: v }))}
                        className="min-h-[44px] sm:min-h-0"
                        style={{
                          display: "flex", alignItems: "baseline", gap: 8,
                          padding: "7px 12px", borderRadius: 8, textAlign: "left", cursor: "pointer",
                          border: profilePatch.activity_level === v ? "1px solid var(--accent)" : "1px solid var(--border-0-10)",
                          background: profilePatch.activity_level === v ? "rgba(99,202,183,0.12)" : "rgba(255,255,255,0.04)",
                          color: profilePatch.activity_level === v ? "var(--accent)" : "var(--text-0-70)",
                        }}
                      >
                        <span style={{ fontSize: 12, fontWeight: 600 }}>{l}</span>
                        <span style={{ fontSize: "var(--fs-xs)", opacity: 0.6 }}>{d}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={handleSaveProfile}
                disabled={savingProfile}
                style={{
                  marginTop: 12, padding: "7px 18px", borderRadius: 8, border: "none",
                  background: "var(--accent)", color: "var(--on-accent)", fontSize: 13, fontWeight: 700, cursor: "pointer",
                  opacity: savingProfile ? 0.6 : 1,
                }}
              >
                {savingProfile ? "Salvando..." : "Salvar e calcular GET"}
              </button>
            </div>
          )}

          {/* Refeições - seleção livre */}
          {(() => {
            const ALL_MEALS = ["Café da manhã", "Lanche 1", "Almoço", "Lanche 2", "Jantar", "Ceia"];
            const selected = aiForm.meal_names;
            const toggle = (name) => {
              setAiForm(f => {
                const isOn = f.meal_names.includes(name);
                if (isOn && f.meal_names.length <= 2) return f; // mínimo 2
                const next = isOn
                  ? f.meal_names.filter(m => m !== name)
                  : [...f.meal_names, name].sort((a, b) => ALL_MEALS.indexOf(a) - ALL_MEALS.indexOf(b));
                return { ...f, meal_names: next };
              });
            };
            return (
              <div>
                <label className={labelCls}>
                  Refeições{" "}
                  <span style={{ fontWeight: 400, color: "var(--text-muted)", fontSize: 12 }}>
                    ({selected.length} selecionadas - mín. 2)
                  </span>
                </label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {ALL_MEALS.map(name => {
                    const on = selected.includes(name);
                    const blocked = on && selected.length <= 2;
                    return (
                      <button
                        key={name} type="button"
                        onClick={() => toggle(name)}
                        disabled={blocked}
                        className="px-3 py-1.5 rounded-full text-sm font-medium transition"
                        style={{
                          background: on ? "rgba(var(--accent-rgb),0.15)" : "var(--surface-primary)",
                          color: on ? "var(--accent)" : "var(--text-muted)",
                          border: on
                            ? "1px solid rgba(var(--accent-rgb),0.4)"
                            : "1px solid var(--border-0-10)",
                          opacity: blocked ? 0.45 : 1,
                          cursor: blocked ? "not-allowed" : "pointer",
                        }}
                      >{name}</button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Macros % */}
          <div>
            <label className={labelCls}>
              Distribuição de macros (%)
              {!macroOk && <span className="text-red-400 ml-2">Total: {totalPct}% - deve somar 100%</span>}
              {macroOk  && <span className="text-[var(--blue)] ml-2"><FontAwesomeIcon icon={faCheck} /> 100%</span>}
            </label>

            {/* Presets - 1 toque em vez de 3 digitações */}
            <div className="flex gap-2 flex-wrap mb-2">
              {[
                { label: "Equilibrado", p: 30, c: 40, f: 30 },
                { label: "+ Proteína",  p: 40, c: 40, f: 20 },
                { label: "Low carb",    p: 35, c: 25, f: 40 },
              ].map(({ label, p, c, f }) => {
                const active = Number(aiForm.protein_pct) === p && Number(aiForm.carb_pct) === c && Number(aiForm.fat_pct) === f;
                return (
                  <button
                    key={label} type="button"
                    onClick={() => setAiForm(fm => ({ ...fm, protein_pct: p, carb_pct: c, fat_pct: f }))}
                    className="px-3 py-1.5 min-h-[44px] sm:min-h-0 rounded-full text-xs font-semibold transition"
                    style={{
                      background: active ? "rgba(var(--accent-rgb),0.12)" : "var(--surface-primary)",
                      color: active ? "var(--blue)" : "var(--text-muted)",
                      border: active ? "1px solid rgba(var(--accent-rgb),0.4)" : "1px solid var(--border-0-10)",
                    }}
                  >
                    {label} <span style={{ opacity: 0.65, fontWeight: 400 }}>{p}·{c}·{f}</span>
                  </button>
                );
              })}
            </div>

            {/* 3 colunas MESMO no celular, de proposito: os tres percentuais
                precisam somar 100 e sao ajustados um em funcao do outro (a
                gordura completa o restante automaticamente). Quebrar em uma
                coluna obrigaria a rolar pra conferir a soma — perde-se
                justamente o que o layout lado a lado entrega. Cabe: ~101px
                por coluna num viewport de 360px, e o rotulo mais longo
                ("Carboidrato") ocupa ~76px no piso de 12px. */}
            <div className="grid grid-cols-3 gap-2">
              {[
                { key: "protein_pct", label: "Proteína",    auto: false },
                { key: "carb_pct",    label: "Carboidrato", auto: false },
                { key: "fat_pct",     label: "Gordura",     auto: true  },
              ].map(({ key, label, auto }) => (
                <div key={key}>
                  <span className="text-xs text-[var(--text-secondary)] block mb-1">{label}</span>
                  <div className="flex items-center gap-1">
                    <input
                      type="number" min={0} max={80} step="any" inputMode="numeric"
                      value={aiForm[key]}
                      onChange={e => {
                        const val = Number(e.target.value);
                        setAiForm(f => {
                          const next = { ...f, [key]: val };
                          if (!auto) {
                            // gordura completa automaticamente o que falta para 100%
                            const p = key === "protein_pct" ? val : Number(f.protein_pct);
                            const c = key === "carb_pct"    ? val : Number(f.carb_pct);
                            next.fat_pct = Math.min(80, Math.max(0, 100 - p - c));
                          }
                          return next;
                        });
                      }}
                      className={inputCls + " text-center"}
                    />
                    <span className="text-[var(--text-secondary)] text-xs">%</span>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs mt-1" style={{ color: "var(--text-0-40)" }}>
              A gordura se ajusta automaticamente ao editar proteína ou carboidrato.
            </p>

            {proteinCheck.outOfRange && (
              <div style={{ marginTop: 10, background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.28)", borderRadius: 10, padding: "10px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
                <p style={{ margin: 0, fontSize: 12.5, color: "var(--feedback-error)", lineHeight: 1.5 }}>
                  Proteína {proteinCheck.gPerKg.toFixed(2)} g/kg fora da faixa fisiológica segura (1.0–3.0 g/kg) para {proteinCheck.weightKg}kg.
                </p>
                <button
                  type="button"
                  onClick={() => setAiForm(f => {
                    const p = proteinCheck.suggestedPct;
                    const c = Number(f.carb_pct);
                    return { ...f, protein_pct: p, fat_pct: Math.min(80, Math.max(0, 100 - p - c)) };
                  })}
                  style={{ alignSelf: "flex-start", background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.4)", color: "var(--feedback-error)", borderRadius: 8, padding: "7px 12px", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}
                >
                  Aplicar sugestão (~{proteinCheck.suggestedPct}% · {proteinCheck.suggestedG}g/dia)
                </button>
              </div>
            )}
          </div>

          <div className={sectionCls}>Personalização</div>

          {/* Preferências */}
          <div>
            <label className={labelCls}>
              Preferências alimentares
              <span style={{ color: "var(--accent)", fontWeight: 600, marginLeft: 4 }}>- a IA usará esses alimentos como primeira escolha</span>
            </label>
            <p className="text-xs mb-2 leading-relaxed" style={{ color: "var(--text-0-40)" }}>
              Alimentos, horários ou hábitos do aluno. A IA usará isso para personalizar as escolhas.
            </p>
            <textarea
              rows={4}
              placeholder={"Ex: gosta de ovos no café, prefere frango e batata doce, toma whey pós-treino às 18h, não gosta de peixe."}
              value={aiForm.preferences}
              onChange={e => setAiForm(f => ({ ...f, preferences: e.target.value }))}
              className={inputCls + " resize-y"}
              style={{ minHeight: 148 }}
            />
          </div>

          {/* Restrições */}
          <div>
            <label className={labelCls}>Restrições / alergias <span className="text-[var(--text-0-60)]">(opcional)</span></label>
            <textarea
              rows={2} placeholder="Ex: intolerância à lactose, sem glúten, não come peixe..."
              value={aiForm.restrictions}
              onChange={e => setAiForm(f => ({ ...f, restrictions: e.target.value }))}
              className={inputCls + " resize-none"}
            />
          </div>



          {/* Botão fixo no fundo - sempre visível independente do scroll */}
          <div style={{ position: "sticky", bottom: -20, marginBottom: -20, background: "linear-gradient(to bottom, transparent 0%, var(--surface-strong) 28%)", paddingTop: 24, paddingBottom: "calc(20px + env(safe-area-inset-bottom))" }}>
            <button
              type="submit"
              disabled={aiLoading || !macroOk || formInvalid || proteinCheck.outOfRange}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm transition"
              style={{
                background: aiLoading || !macroOk || formInvalid || proteinCheck.outOfRange ? "rgba(var(--accent-rgb),0.18)" : "var(--accent)",
                color: aiLoading || !macroOk || formInvalid || proteinCheck.outOfRange ? "rgba(var(--accent-rgb),0.55)" : "var(--text-primary)",
                cursor: aiLoading || !macroOk || formInvalid || proteinCheck.outOfRange ? "not-allowed" : "pointer",
                boxShadow: aiLoading || !macroOk || formInvalid || proteinCheck.outOfRange ? "none" : "0 4px 20px rgba(var(--accent-rgb),0.3)",
              }}
            >
              {aiLoading
                ? <><FontAwesomeIcon icon={faSpinner} spin /> {aiMealPreviews.length > 0 ? `Montando ${aiMealPreviews[aiMealPreviews.length - 1]}…` : "Gerando dieta…"}</>
                : <><FontAwesomeIcon icon={faWandMagicSparkles} /> Gerar dieta com IA</>}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // ── View: preview da dieta gerada pela IA ──────────────────────────────────
  if (view === "ai-preview" && aiDraft) {
    const GOAL_LABEL = { cutting: "Cutting", manutencao: "Manutenção", bulking: "Bulking", saude: "Saúde" };
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <button onClick={() => setView("ai")} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition text-sm shrink-0 min-h-[44px] sm:min-h-0 flex items-center">← Editar</button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <FontAwesomeIcon icon={faWandMagicSparkles} className="text-[var(--blue)] text-xs" />
              <span className="text-xs text-[var(--blue)] font-semibold uppercase tracking-wide">Dieta gerada por IA</span>
            </div>
            <h3 className="text-sm font-bold text-[var(--text-primary)] truncate">{aiDraft.name}</h3>
            <p className="text-xs text-[var(--text-secondary)]">{GOAL_LABEL[aiDraft.goal] || aiDraft.goal}</p>
          </div>
        </div>

        {/* Resumo de macros */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            { label: "kcal",  val: Math.round(aiDraft.total_kcal),     color: "var(--feedback-warning)" },
            { label: "Prot",  val: `${Math.round(aiDraft.total_protein_g)}g`, color: "var(--input-border-focus)" },
            { label: "Carb",  val: `${Math.round(aiDraft.total_carbs_g)}g`,   color: "var(--feedback-warning)" },
            { label: "Gord",  val: `${Math.round(aiDraft.total_fat_g)}g`,     color: "var(--text-primary)" },
          ].map(({ label, val, color }) => (
            <div key={label} className="rounded-lg p-2 text-center" style={{ background: "var(--border-0-04)" }}>
              <div className="text-xs font-bold" style={{ color }}>{val}</div>
              <div className="text-xs text-[var(--text-secondary)] mt-0.5">{label}</div>
            </div>
          ))}
        </div>

        {/* Refeições */}
        <div className="space-y-2">
          {aiDraft.meals.map((meal) => (
            <div key={meal.order} className="rounded-xl overflow-hidden border border-[var(--border-0-08)]" style={{ background: "var(--surface-primary)" }}>
              <div className="flex items-center justify-between px-4 py-2.5" style={{ background: "var(--surface-primary)", borderBottom: "1px solid var(--border-0-10)" }}>
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faUtensils} className="text-[var(--blue)] text-xs" />
                  <span className="text-sm font-bold text-[var(--text-primary)]">{meal.name}</span>
                </div>
                <span className="text-xs text-[var(--text-secondary)]">{Math.round(meal.meal_kcal)} kcal</span>
              </div>
              <div>
                {meal.items.map((item, iIdx) => (
                  <div key={iIdx} style={{ borderBottom: iIdx < meal.items.length - 1 ? "1px solid var(--border-0-08)" : "none" }}>
                    {/* Alimento principal */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 16px" }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }} className="truncate">{item.food_name}</span>
                      <span style={{
                        fontSize: 13, fontWeight: 700, color: "var(--accent)", flexShrink: 0, marginLeft: 12,
                        background: "rgba(var(--accent-rgb),0.18)", borderRadius: 999, padding: "2px 10px",
                      }}>
                        {item.quantity_g}<span style={{ fontSize: "var(--fs-xs)", fontWeight: 400, marginLeft: 2, opacity: 0.7 }}>{item.unit}</span>
                      </span>
                    </div>
                    {/* Substitutos */}
                    {item.substitutes?.length > 0 && (
                      <div style={{ paddingBottom: 6 }}>
                        {item.substitutes.map((sub, sIdx) => (
                          <div key={sIdx} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "4px 16px 4px 28px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                              <span style={{ fontSize: "var(--fs-xs)", color: "var(--text-secondary)", fontStyle: "italic", flexShrink: 0 }}>ou</span>
                              <span style={{ fontSize: 12, color: "var(--text-secondary)" }} className="truncate">{sub.food_name}</span>
                            </div>
                            <span style={{ fontSize: "var(--fs-xs)", fontWeight: 600, color: "var(--accent)", flexShrink: 0, marginLeft: 12, background: "rgba(255,255,255,0.04)", borderRadius: 999, padding: "1px 8px" }}>
                              {sub.quantity_g}<span style={{ fontSize: "var(--fs-xs)", fontWeight: 400, marginLeft: 2, opacity: 0.7 }}>{sub.unit}</span>
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Banner de auto-save */}
        <div style={{ background: "rgba(var(--accent-rgb),0.05)", border: "1px solid rgba(var(--accent-rgb),0.12)", borderRadius: 10, padding: "10px 14px", fontSize: 12, color: "var(--feedback-success)", display: "flex", alignItems: "center", gap: 8 }}>
          <FontAwesomeIcon icon={faCheck} style={{ color: "var(--accent)" }} />
          <span>Dieta <strong style={{ color: "var(--accent)" }}>salva automaticamente</strong>. Você pode editar os itens quando quiser.</span>
        </div>

        {/* Ações */}
        <div className="flex gap-2 pt-1">
          <button
            onClick={() => { if (aiSavedDiet) { setSelectedDiet(aiSavedDiet); setView("edit"); } }}
            disabled={!aiSavedDiet}
            className="flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-semibold transition"
            style={{
              background: "var(--border-0-06)",
              color: aiSavedDiet ? "var(--text-primary)" : "var(--text-0-40)",
              border: "1px solid var(--border-0-10)",
              cursor: aiSavedDiet ? "pointer" : "not-allowed",
            }}
          >
            <FontAwesomeIcon icon={faPenToSquare} /> Editar dieta
          </button>
          <button
            onClick={onClose}
            className="flex-[1.4] flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-bold transition"
            style={{ background: "var(--accent)", color: "var(--on-accent)", cursor: "pointer" }}
          >
            <FontAwesomeIcon icon={faCheck} /> Concluir
          </button>
        </div>
      </div>
    );
  }

  if (view === "edit" && selectedDiet) {
    return <DietEditor diet={selectedDiet} foods={foods} students={students} isManager={isManager} onSaved={handleSaved} onCancel={() => setView("list")} />;
  }
  if (view === "detail" && selectedDiet) {
    const diet = diets.find((d) => d.id === selectedDiet.id) || selectedDiet;
    const owner = ownerLabel(diet);

    if (isAluno) {
      // ── Vista leitura para aluno - estilo treinoio ────────────────────────
      // Aluno self-service Plus sempre tem como voltar pra lista (gerar outra
      // dieta), mesmo com só 1 — aluno gerenciado pelo professor só vê o
      // botão quando há mais de uma (não tem como criar, não precisa do link).
      const hasManyDiets = diets.length > 1 || canCreateOwnDiet;
      const GOAL_LABEL = { cutting: "Cutting", manutencao: "Manutenção", bulking: "Bulking", saude: "Saúde" };

      // Opções de cada item: [item, ...substitutes]. chosenIdx = índice selecionado.
      const allOptions = (item) => [item, ...(item.substitutes || [])];

      return (
        <div className="space-y-4">
          {/* Cabeçalho */}
          <div className="flex items-center gap-3">
            {hasManyDiets && (
              <button onClick={() => setView("list")} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition text-sm shrink-0 min-h-[44px] sm:min-h-0 flex items-center">← Outras</button>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-xs text-[var(--blue)] font-semibold uppercase tracking-wide">Minha dieta</p>
              <h3 className="text-base font-bold text-[var(--text-primary)] truncate">{diet.name || `Dieta ${diet.date}`}</h3>
              <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                {diet.date}{diet.weight_kg ? ` · ${diet.weight_kg} kg` : ""}
                {diet.goal && ` · ${GOAL_LABEL[diet.goal] || diet.goal}`}
              </p>
            </div>
            {canCreateOwnDiet && (
              <button
                onClick={() => { setSelectedDiet(diet); setView("edit"); }}
                className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition shrink-0"
                style={{ background: "rgba(var(--accent-rgb),0.12)", color: "var(--accent)", border: "1px solid rgba(var(--accent-rgb),0.25)" }}
              >
                <FontAwesomeIcon icon={faPenToSquare} /> Editar
              </button>
            )}
          </div>

          {/* Observações do professor */}
          {diet.notes && (
            <div className="bg-[var(--surface-strong)] border border-[var(--border-0-10)] rounded-lg px-3 py-2">
              <p className="text-xs text-[var(--blue)] font-semibold mb-0.5">Observações</p>
              <p className="text-sm text-[var(--text-0-70)]">{diet.notes}</p>
            </div>
          )}

          {/* Refeições */}
          <div className="space-y-3">
            {diet.meals?.filter((m) => m.items.length > 0).map((meal) => (
              <div key={meal.id} className="rounded-2xl overflow-hidden border border-[var(--border-0-08)]" style={{ background: "var(--surface-primary)" }}>
                {/* Header */}
                <div className="flex items-center gap-2 px-4 py-3" style={{ background: "var(--surface-primary)", borderBottom: "1px solid var(--border-0-10)" }}>
                  <FontAwesomeIcon icon={faUtensils} className="text-[var(--blue)] text-xs" />
                  <span className="text-sm font-bold text-[var(--text-primary)]">{meal.name}</span>
                </div>

                {/* Lista de itens - alimento principal + substituições */}
                <div>
                  {meal.items.map((item, iIdx) => {
                    const opts = allOptions(item);
                    const main = opts[0];
                    const subs = opts.slice(1);
                    const mainFood = main?.food;
                    const mainQty  = main?.quantity_g ?? 100;
                    const mainUnit = main?.unit || "g";
                    const isLast   = iIdx === meal.items.length - 1;

                    return (
                      <div key={item.id} style={{ borderBottom: isLast ? "none" : "1px solid var(--border-0-08)" }}>
                        {/* Alimento principal */}
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px" }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }} className="truncate">
                            {mainFood?.name || "-"}
                          </span>
                          <span style={{
                            fontSize: 13, fontWeight: 700, color: "var(--accent)", flexShrink: 0, marginLeft: 12,
                            background: "rgba(var(--accent-rgb),0.18)", borderRadius: 999, padding: "2px 10px",
                          }}>
                            {mainQty}<span style={{ fontSize: "var(--fs-xs)", fontWeight: 400, marginLeft: 2, opacity: 0.7 }}>{mainUnit}</span>
                          </span>
                        </div>

                        {/* Substituições */}
                        {subs.length > 0 && (
                          <div style={{ paddingBottom: 6 }}>
                            {subs.map((sub, sIdx) => {
                              const food = sub?.food;
                              const qty  = sub?.quantity_g ?? 100;
                              const unit = sub?.unit || "g";
                              return (
                                <div key={sIdx} style={{
                                  display: "flex", alignItems: "center", justifyContent: "space-between",
                                  padding: "5px 16px 5px 28px",
                                }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                                    <span style={{ fontSize: "var(--fs-xs)", color: "var(--text-secondary)", fontStyle: "italic", flexShrink: 0 }}>ou</span>
                                    <span style={{ fontSize: 12, color: "var(--text-secondary)", fontWeight: 400 }} className="truncate">
                                      {food?.name || "-"}
                                    </span>
                                  </div>
                                  <span style={{
                                    fontSize: "var(--fs-xs)", fontWeight: 600, color: "var(--accent)", flexShrink: 0, marginLeft: 12,
                                    background: "rgba(255,255,255,0.04)", borderRadius: 999, padding: "1px 8px",
                                  }}>
                                    {qty}<span style={{ fontSize: "var(--fs-xs)", fontWeight: 400, marginLeft: 2, opacity: 0.7 }}>{unit}</span>
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Nenhuma refeição com itens */}
          {diet.meals?.every((m) => m.items.length === 0) && (
            <p className="text-center text-[var(--text-secondary)] text-sm py-6">Nenhum alimento cadastrado nesta dieta.</p>
          )}

          {/* Navegação entre dietas (quando há mais de uma) */}
          {hasManyDiets && (
            <div className="pt-1 border-t border-[var(--border-0-10)]">
              <p className="text-xs text-[var(--text-secondary)] mb-2">Outras dietas</p>
              <div className="flex flex-col gap-1">
                {diets.filter((d) => d.id !== diet.id).map((d) => {
                  const m = calcMacros(d.meals || [], d.weight_kg);
                  return (
                    <button
                      key={d.id}
                      onClick={() => setSelectedDiet(d)}
                      className="flex items-center justify-between px-3 py-2 bg-[var(--surface-secondary)] rounded-lg border border-[var(--border-0-10)] hover:border-[var(--border-0-15)] transition text-left"
                    >
                      <div>
                        <p className="text-sm text-[var(--text-0-70)]">{d.name || `Dieta ${d.date}`}</p>
                        <p className="text-xs text-[var(--text-secondary)]">{d.date}</p>
                      </div>
                      <span className="text-xs text-[var(--text-secondary)]">{m.kcal_total} kcal</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      );
    }

    // ── Vista de detalhe para professor/admin ─────────────────────────────
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-[var(--text-primary)] flex-1 truncate">{diet.name || `Dieta ${diet.date}`}</h3>
          <button
            onClick={() => setView("list")}
            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition shrink-0"
            style={{ background: "var(--border-0-06)", color: "var(--text-muted)", border: "1px solid var(--border-0-10)" }}
          >
            ← Voltar
          </button>
          <button
            onClick={() => downloadDietPdf(diet)}
            disabled={pdfLoading}
            title="Baixar a dieta em PDF com a sua marca"
            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition shrink-0 disabled:opacity-60"
            style={{ background: "var(--border-0-06)", color: "var(--text-primary)", border: "1px solid var(--border-0-10)" }}
          >
            <FontAwesomeIcon icon={faFilePdf} /> {pdfLoading ? "Gerando..." : "PDF"}
          </button>
          <button
            onClick={() => { setSelectedDiet(diet); setView("edit"); }}
            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition shrink-0"
            style={{ background: "rgba(var(--accent-rgb),0.12)", color: "var(--accent)", border: "1px solid rgba(var(--accent-rgb),0.25)" }}
          >
            <FontAwesomeIcon icon={faPenToSquare} /> Editar
          </button>
        </div>
        {owner && (
          <div className="flex items-center gap-1 text-xs text-blue-400 bg-blue-400/10 rounded px-2 py-1">
            <FontAwesomeIcon icon={faUser} size="xs" />
            <span>{owner}</span>
          </div>
        )}
        <div>
          <p className="text-xs text-[var(--text-secondary)] mb-2">{diet.date} {diet.weight_kg ? `· ${diet.weight_kg} kg` : ""}</p>
          <MacroTable meals={diet.meals} weightKg={diet.weight_kg} />
        </div>
        <div className="space-y-3">
          {diet.meals?.map((meal) => (
            <div key={meal.id} className="bg-[var(--surface-secondary)] border border-[var(--border-0-10)] rounded-lg overflow-hidden">
              <p className="text-xs font-semibold text-[var(--blue)] px-3 py-2 bg-[var(--surface-strong)]">{meal.name}</p>
              {meal.items.length === 0
                ? <p className="text-xs text-[var(--text-0-60)] px-3 py-2 italic">Vazia - clique em Editar para adicionar alimentos</p>
                : <div className="divide-y divide-[var(--border-0-10)]">
                    {meal.items.map((item) => (
                      <div key={item.id}>
                        <div className="flex justify-between px-3 py-1.5 text-sm">
                          <span className="text-[var(--text-0-70)]">{item.food?.name}</span>
                          <span className="text-[var(--text-secondary)]">{item.quantity_g}{item.unit || "g"}</span>
                        </div>
                        {(item.substitutes || []).map((sub, si) => (
                          <div key={si} className="flex justify-between pl-7 pr-3 py-1 text-xs bg-[var(--surface-strong)]">
                            <span className="text-[var(--text-secondary)]">Sub {si + 1}: {sub.food?.name}</span>
                            <span className="text-[var(--text-0-60)]">{sub.quantity_g}{sub.unit || "g"}</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
              }
            </div>
          ))}
        </div>
      </div>
    );
  }

  // list view
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-[var(--text-0-70)] shrink-0">
          {filterUserId && students.find((s) => s.id === Number(filterUserId))
            ? `Dietas de ${students.find((s) => s.id === Number(filterUserId)).name}`
            : isAluno ? "Minhas dietas" : "Dietas"}
        </h3>
        {(isManager || canCreateOwnDiet) && (
          <div className="flex gap-2 shrink-0">
            <button
              disabled={checkingAiLimit}
              onClick={async () => {
                // Pré-checagem só faz sentido quando o alvo já é conhecido —
                // aluno gera sempre pra si mesmo; professor escolhe o aluno
                // DENTRO do formulário, então não há quem checar ainda aqui.
                // Evita o usuário preencher tudo pra só no fim descobrir que
                // está bloqueado pelo limite mensal (ver docs/07-issues.md).
                if (!isManager) {
                  setCheckingAiLimit(true);
                  try {
                    const { data } = await api.get(`/ai/diet-last-usage/${currentUserId}`);
                    if (!data.can_generate) {
                      const nextDate = data.next_available_at
                        ? new Date(data.next_available_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })
                        : null;
                      toast.error(nextDate
                        ? `Limite mensal de geração por IA atingido. Próxima geração disponível em ${nextDate}.`
                        : "Limite mensal de geração por IA atingido. Edite sua dieta existente ou aguarde o próximo mês.");
                      return;
                    }
                  } catch {
                    // Falha na pré-checagem não deve travar o fluxo — o backend
                    // ainda valida no submit (POST /generate-diet).
                  } finally {
                    setCheckingAiLimit(false);
                  }
                }
                setAiForm(f => ({ ...f, student_id: filterUserId || "" }));
                setAiError(""); setAiDraft(null);
                setView("ai");
              }}
              className="flex items-center gap-1 text-xs px-3 py-1.5 rounded font-semibold transition"
              style={{
                background: "rgba(var(--accent-rgb),0.15)", color: "var(--accent)", border: "1px solid rgba(var(--accent-rgb),0.3)",
                opacity: checkingAiLimit ? 0.6 : 1, cursor: checkingAiLimit ? "not-allowed" : "pointer",
              }}
            >
              <FontAwesomeIcon icon={checkingAiLimit ? faSpinner : faWandMagicSparkles} spin={checkingAiLimit} /> Nova dieta com IA
            </button>
            {isManager && (
              <button onClick={() => setView("new")} className="flex items-center gap-1 text-xs bg-[var(--blue)] text-[var(--blue-on)] px-3 py-1.5 rounded font-semibold hover:bg-[var(--blue-hover)] transition">
                <FontAwesomeIcon icon={faPlus} /> Nova dieta
              </button>
            )}
          </div>
        )}
      </div>

      {/* Filtro de aluno - professor/admin */}
      {isManager && students.length > 0 && (
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faUser} className="text-[var(--text-secondary)] text-xs shrink-0" />
          <select
            value={filterUserId}
            onChange={(e) => setFilterUserId(e.target.value)}
            className="flex-1 bg-[var(--surface-secondary)] border border-[var(--border-0-15)] rounded px-2 py-1 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[var(--blue)]"
          >
            <option value="" style={SELECT_OPTION_STYLE}>Todos (meus + alunos)</option>
            <option value={currentUserId} style={SELECT_OPTION_STYLE}>Minhas dietas</option>
            {students.map((s) => (
              <option key={s.id} value={s.id} style={SELECT_OPTION_STYLE}>{s.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Modal de duplicar dieta */}
      {cloningDiet && (
        <div className="bg-[var(--surface-strong)] border border-[var(--border-0-15)] rounded-xl p-4 space-y-3">
          <p className="text-sm font-semibold text-[var(--blue)]">
            <FontAwesomeIcon icon={faCopy} className="mr-1.5" />
            Duplicar: <span className="text-[var(--text-primary)]">{cloningDiet.name || cloningDiet.date}</span>
          </p>
          <div className="grid grid-cols-1 gap-2">
            <div>
              <label className="block text-xs text-[var(--text-secondary)] mb-1">Nova data</label>
              <SplitDateInput
                value={cloneDate}
                onChange={setCloneDate}
                inputClassName="w-full bg-[var(--surface-secondary)] border border-[var(--border-0-15)] rounded px-2 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--blue)]"
              />
            </div>
            {isManager && students.length > 0 && (
              <div>
                <label className="block text-xs text-[var(--text-secondary)] mb-1">Para aluno</label>
                <select
                  value={cloneUserId}
                  onChange={(e) => setCloneUserId(e.target.value)}
                  className="w-full bg-[var(--surface-secondary)] border border-[var(--border-0-15)] rounded px-2 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--blue)]"
                >
                  <option value={currentUserId} style={SELECT_OPTION_STYLE}>Para mim</option>
                  {students.map((s) => (
                    <option key={s.id} value={s.id} style={SELECT_OPTION_STYLE}>{s.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleClone}
              disabled={cloning || !cloneDate}
              className="flex-1 bg-[var(--blue)] text-[var(--blue-on)] text-sm font-semibold py-1.5 rounded hover:bg-[var(--blue-hover)] transition disabled:opacity-50"
            >
              {cloning ? "Duplicando…" : "Duplicar"}
            </button>
            <button onClick={() => setCloningDiet(null)} className="flex-1 bg-[var(--surface-0-60)] text-[var(--text-0-70)] text-sm py-1.5 rounded hover:bg-[var(--surface-0-60)] transition">
              Cancelar
            </button>
          </div>
        </div>
      )}

      {loading && <p className="text-center text-[var(--text-secondary)] text-sm py-8">Carregando…</p>}
      {!loading && diets.length === 0 && (
        <p className="text-center text-[var(--text-secondary)] text-sm py-8">Nenhuma dieta encontrada.</p>
      )}

      {/* Friction delete overlay */}
      {confirmDeleteDiet && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.45)" }}
          onClick={(e) => { if (e.target === e.currentTarget) setConfirmDeleteDiet(null); }}
        >
          <div style={{ background: "var(--surface-primary)", border: "1px solid var(--border-0-10)", borderRadius: 16, padding: "24px 20px", width: "90%", maxWidth: 420 }}>
            <p style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)", marginBottom: 6 }}>
              Excluir "{confirmDeleteDiet.name || confirmDeleteDiet.date}"?
            </p>
            {confirmDeleteDiet.ai_generated && (
              <p style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 16 }}>
                Esta dieta foi gerada por IA. Considere <strong style={{ color: "var(--accent)" }}>editar</strong> em vez de excluir - cada nova geração tem custo e consome seu limite mensal.
              </p>
            )}
            {!confirmDeleteDiet.ai_generated && (
              <p style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 16 }}>
                Esta ação é irreversível.
              </p>
            )}
            <div className="flex flex-col gap-2">
              {confirmDeleteDiet.ai_generated && (
                <button
                  onClick={() => { setSelectedDiet(confirmDeleteDiet); setConfirmDeleteDiet(null); setView("edit"); }}
                  style={{ background: "var(--accent)", color: "var(--on-accent)", borderRadius: 10, padding: "10px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", border: "none", width: "100%" }}
                >
                  <FontAwesomeIcon icon={faPenToSquare} /> Editar dieta em vez de excluir
                </button>
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => setConfirmDeleteDiet(null)}
                  style={{ flex: 1, background: "var(--surface-secondary)", color: "var(--text-muted)", border: "1px solid var(--border-0-10)", borderRadius: 8, padding: "8px 12px", fontSize: 13, cursor: "pointer" }}
                >
                  Cancelar
                </button>
                <button
                  onClick={() => executeDeleteDiet(confirmDeleteDiet)}
                  disabled={deleting}
                  style={{ flex: 1, background: "rgba(239,68,68,0.12)", color: "var(--feedback-error)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: 8, padding: "8px 12px", fontSize: 13, fontWeight: 600, cursor: deleting ? "not-allowed" : "pointer", opacity: deleting ? 0.5 : 1 }}
                >
                  {deleting ? "Excluindo…" : "Excluir mesmo assim"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2 max-h-[460px] overflow-y-auto pr-1">
        {diets.map((diet) => {
          const m = calcMacros(diet.meals || [], diet.weight_kg);
          const owner = ownerLabel(diet);
          return (
            <div key={diet.id} className="bg-[var(--surface-strong)] border border-[var(--border-0-10)] rounded-lg px-3 py-3 hover:border-[var(--border-0-20)] transition">
              <div className="flex items-start justify-between gap-2">
                <button onClick={() => { setSelectedDiet(diet); setView("detail"); }} className="flex-1 text-left">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-[var(--text-primary)]">{diet.name || `Dieta - ${diet.date}`}</p>
                    {owner && (
                      <span className="text-[10px] text-blue-400 bg-blue-400/10 px-1.5 py-0.5 rounded">
                        <FontAwesomeIcon icon={faUser} size="xs" className="mr-0.5" />{owner}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[var(--text-secondary)] mt-0.5">{diet.date} {diet.weight_kg ? `· ${diet.weight_kg} kg` : ""}</p>
                  <p className="text-xs text-[var(--text-secondary)] mt-1">
                    P: <span className="text-[var(--blue)]">{m.protein_total.g}g</span> &nbsp;
                    C: <span className="text-blue-400">{m.carbs.g}g</span> &nbsp;
                    G: <span className="text-yellow-400">{m.fat.g}g</span> &nbsp;
                    <span className="text-[var(--text-secondary)]">- {m.kcal_total} kcal</span>
                  </p>
                </button>
                {!isAluno && (
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => openClone(diet)} title="Duplicar dieta" className="text-[var(--text-secondary)] hover:text-blue-400 p-4 -m-3 transition">
                      <FontAwesomeIcon icon={faCopy} size="sm" />
                    </button>
                    <button onClick={() => { setSelectedDiet(diet); setView("edit"); }} className="text-[var(--text-secondary)] hover:text-[var(--blue)] p-4 -m-3 transition">
                      <FontAwesomeIcon icon={faPenToSquare} size="sm" />
                    </button>
                    <button onClick={() => handleDelete(diet)} className="text-[var(--text-secondary)] hover:text-red-400 p-4 -m-3 transition">
                      <FontAwesomeIcon icon={faTrashCan} size="sm" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Modal principal ──────────────────────────────────────────────────────────

export default function NutritionModal({ open, onClose, currentUser, initialStudentId = null, canCreateOwnDiet = false }) {
  const [tab, setTab]             = useState("diets");
  const [foods, setFoods]         = useState([]);
  const [students, setStudents]   = useState([]);
  const [loadingFoods, setLoadingFoods] = useState(true);
  const [isGeneratingDiet, setIsGeneratingDiet] = useState(false);

  const safeClose = () => { if (!isGeneratingDiet) onClose(); };

  // Guarda no CALLBACK (safeClose ja checa isGeneratingDiet), nao na condicao:
  // alternar a condicao faria o cleanup do hook navegar pra tras a cada
  // geracao de dieta. Ver hooks/useHistoryBackClose.js.
  useHistoryBackClose(open, safeClose);

  const isManager = currentUser?.role === "professor" || currentUser?.role === "admin";
  const isAluno   = currentUser?.role === "aluno";
  // Aluno self-service Plus também precisa do catálogo — usa pra escolher/
  // trocar itens no editor após gerar a dieta com IA (mesmo fluxo do professor).
  const needsFoods = !isAluno || canCreateOwnDiet;

  useEffect(() => {
    if (!open) return;
    setLoadingFoods(true);
    const foodsPromise    = needsFoods ? api.get("/nutrition/foods")   : Promise.resolve(null);
    const studentsPromise = isManager  ? api.get("/users/students", { params: { scope: "org", limit: 500 } }) : Promise.resolve(null);
    Promise.all([foodsPromise, studentsPromise])
      .then(([foodsRes, studentsRes]) => {
        if (foodsRes)    setFoods(foodsRes.data);
        if (studentsRes) setStudents(studentsRes.data);
      })
      .catch(() => {})
      .finally(() => setLoadingFoods(false));
  }, [open, isManager, needsFoods]);

  if (!open) return null;

  const TABS = isAluno
    ? []
    : [
        { key: "diets",  label: "Dietas",    icon: faChartPie   },
        { key: "foods",  label: "Alimentos", icon: faAppleWhole },
      ];

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ backgroundColor: SUPPORTS_BACKDROP_FILTER ? "rgba(10,18,35,0.45)" : "rgba(10,18,35,0.68)", ...(SUPPORTS_BACKDROP_FILTER ? { WebkitBackdropFilter: "blur(8px)", backdropFilter: "blur(8px)" } : {}), height: "100dvh", maxHeight: "100svh", paddingBottom: "max(env(safe-area-inset-bottom, 16px), 16px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) safeClose(); }}
    >
      <div
        className="relative w-full max-w-2xl max-h-[90vh] flex flex-col rounded-2xl overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-label="Nutrição"
        style={{ maxHeight: "92svh", backgroundColor: "var(--surface-primary)", border: "1px solid var(--border-0-10)", boxShadow: "0 25px 60px rgba(0,0,0,0.7)" }}
      >
        <ModalHeader onClose={safeClose} disabled={isGeneratingDiet} />
        {/* Header */}
        <div className="flex items-center px-5 py-3 border-b border-[var(--border-0-10)]">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faUtensils} className="text-[var(--blue)]" />
            <h2 className="text-base font-bold text-[var(--text-primary)]">Nutrição</h2>
          </div>
        </div>

        {/* Tabs - ocultas para aluno */}
        {!isAluno && (
          <div className="flex border-b border-[var(--border-0-10)]">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition ${
                  tab === t.key
                    ? "text-[var(--blue)] border-b-2 border-[var(--blue)]"
                    : "text-[var(--text-secondary)] hover:text-[var(--text-0-70)]"
                }`}
              >
                <FontAwesomeIcon icon={t.icon} size="sm" />
                {t.label}
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {loadingFoods ? (
            <p className="text-center text-[var(--text-secondary)] text-sm py-8">Carregando…</p>
          ) : !isAluno && tab === "foods" ? (
            <FoodsTab foods={foods} onFoodsChange={setFoods} isManager={isManager} />
          ) : (
            <DietsTab
              foods={foods}
              students={students}
              isManager={isManager}
              isAluno={isAluno}
              canCreateOwnDiet={canCreateOwnDiet}
              currentUserId={currentUser?.id}
              initialStudentId={initialStudentId}
              onGeneratingChange={setIsGeneratingDiet}
              onClose={safeClose}
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
